import { useState, useEffect, useRef, Component } from "react";
import BrainConsole, { buildCanonical, deriveCategory } from "./admin-brain";
import { DB1_MASTER_KEY, partitionPromotion, brainOnlyNote } from "./admin-promotion";
import { isTrainingRecord } from "./training-mode";  // [WPT1 2026-08-06] never-learn: training work is never promotable
import { T } from "./design/tokens";              // [D8 2026-06-10] design tokens
import { Button, Tabs, Card, Caps, Pill, Tag, Num, StatCard, SectionTitle, Input, Select } from "./design/kit";  // [D8 2026-06-10] kit primitives · [SUBMISSIONS-REDESIGN 2026-06-16] +Input/Select (live)

// [D8 2026-06-10] Legacy `C` palette now derives from the LIGHT design tokens, so all the
// still-inline-styled admin content goes light + cohesive (no dark-on-light) while I convert
// tabs onto the kit proper. (T is light by default — see tokens.js DEFAULT_MODE.)
const C = {
  bg: T.color.bg, card: T.color.surface, card2: T.color.surfaceRaised, card3: T.color.surfaceHover,
  border: T.color.border, border2: T.color.borderStrong, text: T.color.text, muted: T.color.textMuted,
  dim: T.color.textFaint, accent: T.color.accent, include: T.color.include, exclude: T.color.exclude,
  review: T.color.review, learned: T.color.violet, cascade: T.color.review,
};

const getToken = () => localStorage.getItem("ez-wins-admin-token") || "";

const DB1_KEY = DB1_MASTER_KEY;
const DB2_KEY = "warranty-ca-assoc-db-v1";
const DB3_KEY = "warranty-ca-lawexcl-db-v1";
const OEM_MAPPINGS_KEY = "parts-oem-mappings-v1";

async function apiFetch(action, body = {}) {
  const res = await fetch("/api/db", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-team-token": getToken() },
    body: JSON.stringify({ action, ...body }),
  });
  if (!res.ok) { let detail = ""; try { const j = await res.json(); detail = j.detail || j.error || ""; } catch { /* non-JSON */ } throw new Error(detail || `${res.status}`); }  // [KB-CHUNK 2026-08-02] prefer the server detail ("Item size has exceeded…"), not just "DB error"  // [ACCOUNTS] surface the server's reason (e.g. "That username already exists"), not just a status code
  return res.json();
}

// [A1 2026-06-10] APPROVE-GATED brain feed — only admin-approved decisions enter the shared brain.
// DB1 line decisions → add-precedent (with canonical/category from the entry's structured); DB2
// association pairs → add-association. grep "A1 2026-06-10".
// [CTO-1 2026-07-03] Failures are no longer silent: brainPost checks res.ok (fetch doesn't throw on
// HTTP errors), feedBrainEntries returns { fed, failed }, and every approve call site surfaces a
// warning when failed > 0 — approval still never blocks, but a brain↔DB1 divergence is now visible.
async function brainPost(action, body) {
  try {
    const res = await fetch("/api/brain", { method: "POST", headers: { "Content-Type": "application/json", "x-team-token": getToken() }, body: JSON.stringify({ action, ...body }) });
    if (!res.ok) { console.warn("brain feed failed:", action, `HTTP ${res.status}`); return false; }
    return true;
  } catch (e) { console.warn("brain feed failed:", action, e?.message); return false; }
}
async function feedBrainEntries(dbKey, entries) {
  const out = { fed: 0, failed: 0 };
  if (!entries?.length) return out;
  if (dbKey === DB1_KEY) {
    for (const e of entries) {
      if (!e || (e.decision !== "Include" && e.decision !== "Exclude")) continue;  // only decisive line precedents
      const s = e.structured || null;
      const ok = await brainPost("add-precedent", { correction: e.correction, cause: e.cause, complaint: e.complaint, decision: e.decision, rationale: e.learningSignal || e.reasoning || "", structured: s, canonical: buildCanonical(s), category: s ? deriveCategory(s) : null, ro: e.ro, line: e.line, roContext: e.roContext || [], partsSale: e.partsSale ?? null, partsCost: e.partsCost ?? null, laborSale: e.laborSale ?? null, laborCost: e.laborCost ?? null, techHours: e.techHours ?? null, wasSystemError: e.predicted != null && e.predicted !== e.decision, state: e.state || "CA" });  // [9b] sibling context + [full-capture] sales data persisted with the precedent; [BRAIN-LOOP1] corrective flag — the human's decision differed from the frozen machine first call (entries without `predicted` → false)
      if (ok) out.fed++; else out.failed++;
    }
  } else if (dbKey === DB2_KEY) {
    for (const e of entries) {
      if (!e || !e.associateCorr || !e.anchorCorr) continue;
      const ok = await brainPost("add-association", { associateCorr: e.associateCorr, anchorCorr: e.anchorCorr, positive: e.positive, inconclusive: e.inconclusive, reasoning: e.reasoning, ro: e.ro, state: e.state || "CA" });
      if (ok) out.fed++; else out.failed++;
    }
  }
  return out;
}
// [CTO-1 2026-07-03] Shared warning text for a partial brain feed.
const brainFeedWarn = (bf) => bf && bf.failed > 0 ? ` — ⚠ ${bf.failed} didn't reach the brain (master DB updated; re-approve later or check /api/brain)` : "";

async function apiGet(key) {
  const res = await fetch(`/api/db?action=get&key=${encodeURIComponent(key)}`, {
    headers: { "x-team-token": getToken() },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
}

function AdminLogin({ onAuth, notice = "" }) {
  const [pw, setPw]   = useState("");
  const [err, setErr] = useState(notice);   // [WPB1 2026-08-04] why the boot check bounced you back here
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/db", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-team-token": pw.trim() },
        body: JSON.stringify({ action: "list-submissions" }),
      });
      if (res.status === 401 || res.status === 403) {
        setErr("Wrong admin password."); setLoading(false); return;
      }
      localStorage.setItem("ez-wins-admin-token", pw.trim());
      onAuth();
    } catch { setErr("Connection error."); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "36px 40px", width: 340 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text, marginBottom: 4 }}>EZ Wins Admin</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 24 }}>Submission review panel</div>
        <input type="password" value={pw} onChange={e => setPw(e.target.value)} onKeyDown={e => e.key === "Enter" && handleLogin()}
          placeholder="Admin password"
          style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, padding: "10px 12px", color: C.text, fontSize: 13, outline: "none", fontFamily: "inherit", marginBottom: 12 }} />
        {err && <div style={{ fontSize: 12, color: C.exclude, marginBottom: 10 }}>⚠ {err}</div>}
        <Button variant="primary" onClick={handleLogin} disabled={loading} style={{ width: "100%", justifyContent: "center", padding: "11px" }}>
          {loading ? "Verifying…" : "Enter →"}
        </Button>
      </div>
    </div>
  );
}

// ── [WPB1 2026-08-04] Service health chip ───────────────────────────────────
// Two outages hid for WEEKS behind features that just quietly stopped working: the OpenAI
// embeddings key (2026-07-07) and the Anthropic key (2026-07-20). Nothing on screen said so.
// On every admin load these three probes run and the header states the truth:
//   AI          → GET /api/claude?health=1 (Anthropic model LIST — exercises the key, spends no
//                 completion; see api/claude.js)
//   Brain       → /api/brain `stats` (Neon Postgres reachable, tables answer)
//   Embeddings  → /api/brain `search` on one short string (OpenAI embedding + pgvector query;
//                 zero results is still healthy — the embedding is what's under test)
// Failures render red with the server's reason on hover. NOTHING here blocks the panel.
const HEALTH_PROBES = [
  { id: "ai", label: "AI", run: async () => {
      const r = await fetch("/api/claude?health=1", { headers: { "x-team-token": getToken() } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      if (!j.ok) throw new Error(j.detail || `HTTP ${j.status || "error"}`);
    } },
  { id: "brain", label: "Brain", run: () => brainProbe("stats", {}) },
  { id: "embed", label: "Embeddings", run: () => brainProbe("search", { text: "warranty classifier health check", k: 1 }) },
];
async function brainProbe(action, extra) {
  const r = await fetch("/api/brain", { method: "POST", headers: { "Content-Type": "application/json", "x-team-token": getToken() }, body: JSON.stringify({ action, ...extra }) });
  if (!r.ok) { let d = ""; try { const j = await r.json(); d = j.detail || j.error || ""; } catch { /* non-JSON */ } throw new Error(d || `HTTP ${r.status}`); }
  await r.json();
}
function HealthChip() {
  const [health, setHealth] = useState(() => HEALTH_PROBES.map(p => ({ id: p.id, label: p.label, state: "checking", detail: "" })));
  const [checking, setChecking] = useState(false);

  const runProbes = async () => {
    setChecking(true);
    setHealth(HEALTH_PROBES.map(p => ({ id: p.id, label: p.label, state: "checking", detail: "" })));
    const results = await Promise.all(HEALTH_PROBES.map(async p => {
      try { await p.run(); return { id: p.id, label: p.label, state: "ok", detail: "" }; }
      catch (e) { return { id: p.id, label: p.label, state: "down", detail: e?.message || "unreachable" }; }
    }));
    setHealth(results);
    setChecking(false);
  };
  useEffect(() => { runProbes(); }, []);

  const down = health.filter(h => h.state === "down");
  return (
    <div onClick={() => { if (!checking) runProbes(); }} title={down.length ? down.map(h => `${h.label}: ${h.detail}`).join("\n") : "All services responding — click to re-check"}
      style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 10px", borderRadius: 999, cursor: checking ? "progress" : "pointer",
        border: `1px solid ${down.length ? T.color.exclude : T.color.border}`, background: down.length ? `${T.color.exclude}12` : T.color.surfaceRaised }}>
      {health.map(h => (
        <span key={h.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: T.size.caption, fontWeight: T.weight.semibold,
          color: h.state === "ok" ? T.color.include : h.state === "down" ? T.color.exclude : T.color.textMuted }}>
          {h.state === "ok" ? "✓" : h.state === "down" ? "✗" : "•"} {h.label}
        </span>
      ))}
    </div>
  );
}

// [SUBMISSIONS-REDESIGN 2026-06-16] relative timestamp for the submissions feed.
function relTime(d) {
  const t = new Date(d).getTime();
  if (!t || Number.isNaN(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const dd = Math.floor(h / 24); if (dd < 30) return `${dd}d ago`;
  return new Date(d).toLocaleDateString();
}

// [SUBMISSIONS-REDESIGN 2026-06-16] per-row error boundary — now that ALL entries render (no 10-cap),
// one malformed legacy record must not blank the whole page. Shows a fallback + the actual error so we
// can identify the bad entry instead of crashing the screen.
class RowBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) {
      return (
        <Card style={{ marginBottom: T.space[3], borderLeft: `3px solid ${T.color.exclude}` }}>
          <div style={{ fontSize: T.size.caption, color: T.color.exclude, marginBottom: 6 }}>⚠ Couldn't render this submission (RO# {String(this.props.ro ?? "?")} · id {String(this.props.id ?? "?")}): {String(this.state.err?.message || this.state.err).slice(0, 160)}</div>
          {this.props.onReject && <Button variant="danger" size="sm" onClick={this.props.onReject}>✗ Reject this entry</Button>}
        </Card>
      );
    }
    return this.props.children;
  }
}

function EntryRow({ entry, dbKey, username, onApprove, onReject, onEditSignal, selected, onToggle }) {
  const isDecisive = ["Include", "Exclude", "Human Review"].includes(entry.decision);
  const isAssoc = !isDecisive && !!entry.associateCorr;  // DB2 association pair (no plain decision)
  const corrective = entry.predicted && entry.predicted !== entry.decision;  // [SUBMISSIONS-REDESIGN] system-got-it-wrong = high-value precedent
  const accent = entry.decision === "Include" ? T.color.include
    : entry.decision === "Exclude" ? T.color.exclude
    : entry.decision === "Human Review" ? T.color.review
    : isAssoc ? (entry.inconclusive ? T.color.review : entry.positive ? T.color.include : T.color.exclude)
    : T.color.border;
  const s = entry.structured;  // [SUBMISSIONS-REDESIGN] 🧩 AI understanding — lets the reviewer judge precedent quality
  const dq = s && Array.isArray(s.disqualifiers) ? s.disqualifiers.filter(d => d && d !== "none") : [];
  // [AI-READ-POPOVER 2026-06-16] compact 🧩 chip (colored by `qualifies` for at-a-glance scan) → click/hover pops the detail.
  const hasAI = !!(s && (s.component || s.action || s.reason));
  const qColor = s && s.qualifies === "yes" ? T.color.include : s && s.qualifies === "no" ? T.color.exclude : T.color.review;
  const [aiPin, setAiPin] = useState(false);
  const [aiHover, setAiHover] = useState(false);
  const showAI = aiPin || aiHover;
  const [editing, setEditing] = useState(false);   // [SIGNAL-EDIT 2026-07-20]
  const [draft, setDraft] = useState("");
  return (
    <Card style={{ marginBottom: T.space[3], padding: `${T.space[4]}px ${T.space[5]}px`, borderLeft: `3px solid ${accent}`, background: selected ? T.color.accentSoft : undefined }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: T.space[3], flexWrap: "wrap" }}>
        <input type="checkbox" checked={!!selected} onChange={onToggle} title="Select for bulk action"
          style={{ width: 16, height: 16, accentColor: T.color.accent, cursor: "pointer", flexShrink: 0 }} />
        {isDecisive ? <Pill status={entry.decision === "Include" ? "include" : entry.decision === "Exclude" ? "exclude" : "review"} size="sm" />
          : isAssoc ? <Tag color={accent}>↔ {entry.inconclusive ? "Assoc ?" : entry.positive ? "Associated" : "Not assoc"}</Tag>
          : <Tag>{entry.decision || "—"}</Tag>}
        <span style={{ fontSize: T.size.caption, color: T.color.textMuted, fontFamily: T.font.mono }}>RO# {entry.ro} · Ln {entry.line}</span>
        {hasAI && (
          <span style={{ position: "relative", display: "inline-flex" }} onMouseEnter={() => setAiHover(true)} onMouseLeave={() => setAiHover(false)}>
            <button type="button" aria-label={`AI read — qualifies ${s.qualifies || "unrated"}`} aria-expanded={showAI}
              onClick={() => setAiPin(p => !p)} onKeyDown={e => { if (e.key === "Escape") setAiPin(false); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 7px", borderRadius: 999, border: `1px solid ${qColor}66`, background: `${qColor}14`, color: qColor, cursor: "pointer", fontFamily: "inherit", fontSize: T.size.caption, fontWeight: T.weight.semibold, lineHeight: 1 }}>
              🧩 <span style={{ width: 6, height: 6, borderRadius: "50%", background: qColor, display: "inline-block" }} />
            </button>
            {showAI && (
              <div role="tooltip" style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 60, width: 300, maxWidth: "78vw", padding: "10px 12px", background: T.color.surfaceRaised, border: `1px solid ${T.color.border}`, borderRadius: T.radius.lg, boxShadow: T.shadow.e3, cursor: "default" }}>
                <div style={{ marginBottom: 6 }}><Caps>🧩 AI understanding</Caps></div>
                <div style={{ fontSize: T.size.sm, color: T.color.text, fontWeight: T.weight.medium, lineHeight: 1.4 }}>{[s.component, s.system, s.action].filter(Boolean).map(String).join(" · ") || "—"}</div>
                {s.completed === false && <div style={{ fontSize: T.size.caption, color: T.color.review, marginTop: 2 }}>not completed</div>}
                {s.qualifies && <div style={{ marginTop: 7 }}><Tag color={qColor}>qualifies: {String(s.qualifies)}</Tag></div>}
                {s.reason && <div style={{ fontSize: T.size.caption, color: T.color.textSecondary, marginTop: 7, lineHeight: 1.4 }}><span style={{ color: T.color.textFaint }}>why: </span>{String(s.reason)}</div>}
                {dq.length > 0 && <div style={{ fontSize: T.size.caption, color: T.color.exclude, marginTop: 5 }}>⚠ disqualifiers: {dq.map(String).join(", ")}</div>}
              </div>
            )}
          </span>
        )}
        {corrective && <Tag color={T.color.violet}>⟲ system said {entry.predicted}</Tag>}
        {/* [WPT1 2026-08-06] Anything that slipped past the client and server refusals says so
            HERE, at the approve gate. The server refuses to promote it too — this is so the
            admin knows why the button will fail, rather than discovering it as an error. */}
        {isTrainingRecord(entry) && <Tag color={T.color.review}>{"🎓 TRAINING — cannot be promoted"}</Tag>}
        {entry.viaBless && <Tag color={T.color.include}>✓ Confirmed auto</Tag>}
        {entry.auditOf && <Tag color={T.color.violet}>🔍 Audit of {entry.auditOf}</Tag>}
        {entry.futureIntent && <Tag color={T.color.review}>📊 Future intent</Tag>}
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 9 }}>
          {username && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: T.size.caption, color: T.color.textMuted }}>
            <span style={{ width: 18, height: 18, borderRadius: "50%", background: `${T.color.accent}20`, color: T.color.accent, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 800 }}>{username[0]?.toUpperCase()}</span>{username}
          </span>}
          <span style={{ fontSize: T.size.caption, color: T.color.textFaint }} title={new Date(entry.date).toLocaleString()}>{relTime(entry.date)}</span>
        </span>
      </div>
      <div style={{ fontSize: T.size.sm, color: T.color.text, marginBottom: 4 }}>
        <span style={{ color: T.color.textMuted }}>Correction: </span>{entry.correction || <em style={{ color: T.color.textFaint }}>blank</em>}
      </div>
      {entry.cause && <div style={{ fontSize: T.size.caption, color: T.color.textMuted, marginBottom: 2 }}><span style={{ color: T.color.textFaint }}>Cause: </span>{entry.cause}</div>}
      {entry.complaint && <div style={{ fontSize: T.size.caption, color: T.color.textMuted, marginBottom: 2 }}><span style={{ color: T.color.textFaint }}>Complaint: </span>{entry.complaint}</div>}
      {entry.associateCorr && (
        <div style={{ fontSize: T.size.caption, marginBottom: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span style={{ color: T.color.textMuted }}>{entry.associateCorr}</span>
          <span style={{ color: T.color.textFaint }}>↔</span>
          <span style={{ color: T.color.include }}>{entry.anchorCorr}</span>
          <Tag color={entry.inconclusive ? T.color.review : entry.positive ? T.color.include : T.color.exclude}>{entry.inconclusive ? "?" : entry.positive ? "associated" : "not associated"}</Tag>
        </div>
      )}
      {/* [SIGNAL-EDIT 2026-07-20] the learning signal, editable at the gate (feedback-writer rules as the hint) */}
      {editing ? (
        <div style={{ marginBottom: T.space[4] }}>
          <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={2} autoFocus
            onKeyDown={e => { if (e.key === "Escape") setEditing(false); }}
            style={{ width: "100%", boxSizing: "border-box", background: T.color.bgInset, border: `1px solid ${T.color.accent}`, borderRadius: T.radius.md, padding: "8px 10px", color: T.color.text, fontFamily: T.font.sans, fontSize: T.size.sm, resize: "vertical" }} />
          <div style={{ fontSize: T.size.caption, color: T.color.textFaint, margin: "4px 0 6px" }}>
            One sentence: the specific part + the actual evidence (fault code / confirmed failure / the disqualifier). No "all X going forward" generalizing.
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <Button variant="secondary" onClick={() => { onEditSignal?.(username, dbKey, entry.id, draft); setEditing(false); }} disabled={!draft.trim()}>Save signal</Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: T.size.sm, color: T.color.textSecondary, marginBottom: T.space[4], fontStyle: "italic", display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span>"{entry.learningSignal || entry.reasoning}"</span>
          {entry._signalEdited && <Tag color={T.color.violet}>✎ edited</Tag>}
          {onEditSignal && <button type="button" onClick={() => { setDraft(entry.learningSignal || entry.reasoning || ""); setEditing(true); }}
            title="Polish this learning signal before it becomes a precedent — edits apply to whatever approve action you use next"
            style={{ background: "transparent", border: "none", color: T.color.accent, cursor: "pointer", fontSize: T.size.caption, fontWeight: T.weight.semibold, fontFamily: "inherit", fontStyle: "normal", padding: 0 }}>✎ Edit signal</button>}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <Button variant="primary" onClick={() => onApprove(entry, dbKey, username)}>✓ Approve → Master DB</Button>
        <Button variant="danger" onClick={() => onReject(entry, dbKey, username)}>✗ Reject</Button>
      </div>
    </Card>
  );
}

// ── Seed KB Tab ───────────────────────────────────────────────────────────
function SeedKBTab({ masterDb, onSeeded }) {
  const [file,    setFile]    = useState(null);
  const [preview, setPreview] = useState(null);
  const [status,  setStatus]  = useState("idle");
  const [results, setResults] = useState(null);
  const [err,     setErr]     = useState("");
  const fileRef = useRef();

  const handleFile = (f) => {
    setFile(f); setStatus("previewing"); setErr(""); setResults(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const kb = JSON.parse(e.target.result);
        if (!kb.db1 || !kb.db2) { setErr("Invalid KB JSON — missing db1/db2."); setStatus("error"); return; }
        const existingIds = {
          [DB1_KEY]: new Set((masterDb[DB1_KEY] || []).map(x => x.id)),
          [DB2_KEY]: new Set((masterDb[DB2_KEY] || []).map(x => x.id)),
          [DB3_KEY]: new Set((masterDb[DB3_KEY] || []).map(x => x.id)),
        };
        const toAdd = {
          db1: (kb.db1 || []).filter(e => !existingIds[DB1_KEY].has(e.id)),
          db2: (kb.db2 || []).filter(e => !existingIds[DB2_KEY].has(e.id)),
          db3: (kb.db3 || []).filter(e => !existingIds[DB3_KEY].has(e.id)),
        };
        setPreview({ kb, toAdd, exportDate: kb.exportDate });
      } catch { setErr("Could not parse JSON file."); setStatus("error"); }
    };
    reader.readAsText(f);
  };

  const handleSeed = async () => {
    if (!preview) return;
    setStatus("seeding"); setErr("");
    try {
      const res = await apiFetch("seed-kb", {
        db1: preview.toAdd.db1,
        db2: preview.toAdd.db2,
        db3: preview.toAdd.db3,
      });
      setResults(res);
      setStatus("done");
      onSeeded();
    } catch (e) { setErr("Seed failed: " + e.message); setStatus("error"); }
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 6 }}>Seed Knowledge Base from Export</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
          Upload a <code style={{ background: C.card3, padding: "1px 5px", borderRadius: 3 }}>KB_data.json</code> exported from the classifier.
          Existing entries are preserved — only new entries (by ID) are added.
        </div>
        <div onClick={() => fileRef.current?.click()}
          style={{ border: `2px dashed ${C.border}`, borderRadius: 10, padding: "32px 20px", textAlign: "center", cursor: "pointer", background: C.card3, marginBottom: 16 }}>
          <input ref={fileRef} type="file" accept=".json" style={{ display: "none" }} onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
          <div style={{ fontSize: 28, marginBottom: 8 }}>📂</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: file ? C.include : C.muted }}>
            {file ? `✓ ${file.name}` : "Click to select KB_data.json"}
          </div>
        </div>
        {preview && status === "previewing" && (
          <div style={{ background: `${C.learned}08`, border: `1px solid ${C.learned}30`, borderRadius: 10, padding: "16px 18px", marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.learned, marginBottom: 10 }}>📋 Preview — what will be added</div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Export date: {new Date(preview.exportDate).toLocaleString()}</div>
            {[
              { label: "DB1 — Flow 1 Patterns", total: preview.kb.db1.length, adding: preview.toAdd.db1.length, color: C.accent },
              { label: "DB2 — Association Pairs", total: preview.kb.db2.length, adding: preview.toAdd.db2.length, color: C.cascade },
              { label: "DB3 — Flow 1 Overrides", total: preview.kb.db3.length, adding: preview.toAdd.db3.length, color: C.learned },
            ].map(row => (
              <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: `1px solid ${C.border2}` }}>
                <div style={{ flex: 1, fontSize: 12, color: C.text }}>{row.label}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{row.total} in file</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: row.adding > 0 ? row.color : C.dim, minWidth: 80, textAlign: "right" }}>
                  {row.adding > 0 ? `+${row.adding} new` : "✓ up to date"}
                </div>
              </div>
            ))}
            <div style={{ marginTop: 14 }}>
              {(preview.toAdd.db1.length + preview.toAdd.db2.length + preview.toAdd.db3.length) === 0
                ? <div style={{ fontSize: T.size.sm, color: T.color.include, fontWeight: T.weight.semibold }}>✓ Live DB is already up to date.</div>
                : <Button variant="primary" size="lg" onClick={handleSeed}>⬆ Seed {preview.toAdd.db1.length + preview.toAdd.db2.length + preview.toAdd.db3.length} new entries →</Button>
              }
            </div>
          </div>
        )}
        {status === "seeding" && <div style={{ color: C.muted, fontSize: 13 }}>Seeding… do not close this tab.</div>}
        {status === "done" && results && (
          <div style={{ background: `${C.include}10`, border: `1px solid ${C.include}40`, borderRadius: 10, padding: "16px 18px" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.include, marginBottom: 8 }}>✅ Seed complete</div>
            {results.results?.map(r => (
              <div key={r.key} style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>{r.label}: added {r.added}, total {r.total}</div>
            ))}
          </div>
        )}
        {err && <div style={{ fontSize: 12, color: C.exclude, marginTop: 8 }}>⚠ {err}</div>}
      </div>
    </div>
  );
}

// ── Corpus Review Tab — mine the decision-log corpus, promote good ones to the brain ──
// The corpus (dlog:*) logs EVERY classification for stats. Only human-confirmed lines are
// brain-eligible; this tab lets an admin review and promote selected records into DB1 (the
// shared brain), turning high-volume auto data into curated knowledge. Re-normalized at
// classify time, so the raw correction is fine to store.
// Load + dedup the whole decision-log corpus (latest snapshot per project:ro:line).
// [WPB1 2026-08-04] Was one strictly-sequential round trip per dlog batch (N+1): with a few hundred
// batches the Corpus and Confidence tabs sat "Loading…" for minutes, and both tabs run this on mount.
// Now a fixed pool of 6 workers pulls from a shared cursor. Results are written back into a
// position-indexed array and flattened in KEY ORDER, so the merged list — and therefore the
// capturedAt tie-break in the dedupe below — is exactly what the sequential loop produced.
// Errors still skip that one batch. Cap of 6 is deliberate: each call is a DynamoDB Get behind a
// serverless function, and the browser only allows ~6 concurrent connections per origin anyway.
const CORPUS_CONCURRENCY = 6;
async function fetchCorpus() {
  const { keys } = await apiFetch("list-decision-logs");
  const list = keys || [];
  const chunks = new Array(list.length).fill(null);
  let cursor = 0;
  const worker = async () => {
    for (let i = cursor++; i < list.length; i = cursor++) {
      try { const { value } = await apiFetch("get-decision-log", { key: list[i] }); chunks[i] = Array.isArray(value) ? value : []; }
      catch { chunks[i] = []; /* skip unreadable batch */ }
    }
  };
  await Promise.all(Array.from({ length: Math.min(CORPUS_CONCURRENCY, list.length) }, worker));
  // [WPT1 2026-08-06] Training records are dropped at the single funnel both the Corpus tab
  // (promotion) and the Confidence tab (stats) read — so practice reps can neither be promoted
  // into the brain nor move a measured number, and there is one place to check that, not two.
  const all = chunks.flatMap(c => c || []).filter(r => !isTrainingRecord(r));
  const byKey = new Map();
  for (const r of all) {
    const id = `${r.projectId}|${r.ro}|${r.line}`;
    const prev = byKey.get(id);
    if (!prev || (r.capturedAt || "") > (prev.capturedAt || "")) byKey.set(id, r);
  }
  return [...byKey.values()].sort((a, b) => (b.capturedAt || "").localeCompare(a.capturedAt || ""));
}

function recordToDB1Entry(rec) {
  const decision = rec.final?.humanDecision || rec.final?.result;
  return {
    id: rec.recordId, date: rec.capturedAt || new Date().toISOString(),
    ro: rec.ro || "", line: rec.line || "",
    correction: rec.outsideFactor ? "" : (rec.correction || ""),
    cause: rec.cause || "", complaint: rec.complaint || "",
    originalResult: rec.predicted?.result || null, decision,
    cccSufficient: rec.pipelineSignals?.cccSufficient ?? null,
    learningSignal: rec.rationale?.text || "", reasoning: rec.rationale?.text || "",
    futureIntent: !!rec.signals?.futureIntent,
    partsSale: rec.partsSale ?? null, partsCost: rec.partsCost ?? null,
    laborSale: rec.laborSale ?? null, laborCost: rec.laborCost ?? null, techHours: rec.techHours ?? null,
    auditOf: rec.auditOf || null, promotedFromCorpus: true,
    structured: rec.structured || null,  // [AUDIT 2026-06-10] carry 🧩 anatomy so the brain feed builds canonical/category
    roContext: rec.roContext || [],  // [9b] sibling-line context rides from corpus → precedent
    predicted: rec.predicted?.result ?? null,  // [CTO-3 2026-07-03] was missing — feedBrainEntries reads e.predicted for the corrects_prediction flag, so corpus promotes never marked corrective precedents
  };
}

function CorpusTab({ masterDb, onPromoted }) {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tier, setTier] = useState("all");
  const [ovr, setOvr] = useState("all");
  const [dec, setDec] = useState("all");
  const [brainOnly, setBrainOnly] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [promoting, setPromoting] = useState(false);
  const [msg, setMsg] = useState("");
  const [fb, setFb] = useState({});  // [CTO-3 2026-07-03] recordId → admin feedback, the upgrade ticket for non-brain-eligible rows

  const loadCorpus = async () => {
    setLoading(true); setErr("");
    try { setRecords(await fetchCorpus()); }
    catch (e) { setErr("Load error: " + e.message); setRecords([]); }
    setLoading(false);
  };
  useEffect(() => { loadCorpus(); }, []);

  const finalDec = r => r.final?.humanDecision || r.final?.result;
  const filtered = (records || []).filter(r => {
    if (tier !== "all" && r.confirmationTier !== tier) return false;
    if (ovr === "yes" && !r.overridden) return false;
    if (ovr === "no" && r.overridden) return false;
    if (dec !== "all" && finalDec(r) !== dec) return false;
    if (brainOnly && !r.brainEligible) return false;
    if (q.trim()) { const s = q.toLowerCase(); if (!`${r.correction} ${r.cause} ${r.complaint} ${r.rationale?.text || ""}`.toLowerCase().includes(s)) return false; }
    return true;
  });

  const existingDB1 = new Set((masterDb[DB1_KEY] || []).map(e => `${e.ro}|${e.line}|${e.decision}`));
  const total = (records || []).length;
  const ovrCount = (records || []).filter(r => r.overridden).length;
  const brainCount = (records || []).filter(r => r.brainEligible).length;

  const toggle = (id) => setSel(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const dc = d => d === "Include" ? C.include : d === "Exclude" ? C.exclude : C.review;

  // [CTO-3 2026-07-03] Upgrade path (Blake): a non-brain-eligible row (bulk-confirmed / auto) can be
  // promoted ONLY by typing feedback — the feedback becomes the precedent's learning signal (a real
  // human rationale), which is exactly what bulk-confirm lacked. No feedback → stays stats-only.
  const feedbackFor = r => (fb[r.recordId] || "").trim();
  const promote = async () => {
    const selRows = filtered.filter(r => sel.has(r.recordId));
    const chosen = selRows.filter(r => r.brainEligible || feedbackFor(r));  // [AUDIT 2026-06-10] no-learn/unconfirmed stay out unless feedback upgrades them
    const skippedNoFb = selRows.length - chosen.length;
    const seen = new Set();
    const entries = chosen.map(r => {
      const e = recordToDB1Entry(r);
      const f = feedbackFor(r);
      if (f) { e.learningSignal = f; e.reasoning = f; e.upgradedWithFeedback = true; }  // admin's words replace the machine rationale
      return e;
    }).filter(e => {
      const k = `${e.ro}|${e.line}|${e.decision}`;
      if (!(e.decision === "Include" || e.decision === "Exclude" || e.decision === "Ambiguous")) return false;
      if (existingDB1.has(k) || seen.has(k)) return false;
      seen.add(k); return true;
    });
    if (!entries.length) {
      const fbNote = skippedNoFb ? `, or missing feedback (${skippedNoFb} unconfirmed selected — type feedback in the row to upgrade)` : "";
      setMsg(`Nothing to promote — selection is empty, all duplicates of the brain, all Human Review${fbNote}.`); return;
    }
    if (!window.confirm(`Promote ${entries.length} record(s) into the brain (DB1)? This becomes shared classification knowledge.`)) return;
    setPromoting(true); setMsg("");
    try {
      const { masterEntries, brainOnlyEntries } = partitionPromotion(DB1_KEY, entries);
      if (masterEntries.length) await apiFetch("approve", { masterKey: DB1_KEY, entries: masterEntries });
      const bf = await feedBrainEntries(DB1_KEY, entries);  // [AUDIT 2026-06-10] Corpus promote must ALSO feed the brain (was DB1-only — A1 gap)
      const skipNote = skippedNoFb ? ` ${skippedNoFb} skipped — unconfirmed lines need feedback to upgrade.` : "";
      setMsg(`✓ Promoted ${masterEntries.length} → master DB1; ${entries.length} → brain.${skipNote}${brainOnlyNote(brainOnlyEntries.length)}${brainFeedWarn(bf)}`);
      setSel(new Set()); setFb({});
      onPromoted?.();
    } catch (e) { setMsg("Promote error: " + e.message); }
    setPromoting(false);
  };

  const selStyle = { background: C.card3, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, fontFamily: "inherit" };

  if (loading) return <div style={{ color: C.muted, fontSize: 13 }}>Loading corpus… (scanning decision logs)</div>;
  if (err) return <div style={{ color: T.color.exclude, fontSize: T.size.sm }}>⚠ {err} <Button variant="ghost" onClick={loadCorpus} style={{ marginLeft: 8 }}>Retry</Button></div>;

  return (
    <div>
      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14, fontSize: 12 }}>
        <span style={{ color: C.muted }}>Corpus: <b style={{ color: C.text }}>{total}</b> lines</span>
        <span style={{ color: C.muted }}>Override rate: <b style={{ color: ovrCount ? C.review : C.include }}>{total ? Math.round((ovrCount / total) * 100) : 0}%</b> ({ovrCount})</span>
        <span style={{ color: C.muted }}>Brain-eligible: <b style={{ color: C.learned }}>{brainCount}</b></span>
        <Button variant="ghost" onClick={loadCorpus} style={{ marginLeft: "auto" }}>↺ Reload</Button>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
        Every classification is logged for stats. <b style={{ color: C.learned }}>Brain-eligible</b> = a human confirmed it (typed a decision or individually ✓ Confirmed) — promote those directly. <b>Bulk-confirmed / auto</b> lines carry no per-line feedback, so to upgrade one to the brain you must <b>type feedback</b> in its row first — your words become the precedent's learning signal. No feedback → stays stats-only.
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <select value={tier} onChange={e => setTier(e.target.value)} style={selStyle}><option value="all">All tiers</option><option value="typed">Typed (decided)</option><option value="blessed">Blessed (✓ confirmed)</option><option value="bulk-blessed">Bulk-confirmed (read-through)</option><option value="auto">Auto (skimmed)</option></select>
        <select value={ovr} onChange={e => setOvr(e.target.value)} style={selStyle}><option value="all">Override: all</option><option value="yes">Overridden</option><option value="no">Not overridden</option></select>
        <select value={dec} onChange={e => setDec(e.target.value)} style={selStyle}><option value="all">Any decision</option><option value="Include">Include</option><option value="Exclude">Exclude</option><option value="Human Review">Human Review</option></select>
        <label style={{ display: "flex", alignItems: "center", gap: 5, color: C.muted, fontSize: 12, cursor: "pointer" }}><input type="checkbox" checked={brainOnly} onChange={e => setBrainOnly(e.target.checked)} /> brain-eligible only</label>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="search CCC / reason…" style={{ ...selStyle, minWidth: 180 }} />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <Button variant="secondary" onClick={() => setSel(new Set(filtered.map(r => r.recordId)))}>Select all filtered ({filtered.length})</Button>
        <Button variant="ghost" onClick={() => setSel(new Set())}>Clear</Button>
        <Button variant="primary" onClick={promote} disabled={promoting || sel.size === 0}>{promoting ? "Promoting…" : `🧠 Promote ${sel.size} to brain`}</Button>
        {msg && <span style={{ fontSize: T.size.sm, fontWeight: T.weight.semibold, color: msg.startsWith("✓") ? T.color.include : T.color.review }}>{msg}</span>}
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ overflowX: "auto", maxHeight: "55vh", overflowY: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead><tr style={{ background: C.card2, position: "sticky", top: 0 }}>{["", "RO / Ln", "Correction", "Pred → Final", "Tier", "Brain", "Signals", "Reason"].map((h, i) => <th key={i} style={{ padding: "7px 9px", textAlign: "left", fontSize: 9, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.slice(0, 500).map(r => {
                const d = finalDec(r); const checked = sel.has(r.recordId);
                return (
                  <tr key={r.recordId} style={{ borderTop: `1px solid ${C.border2}`, background: checked ? `${C.accent}10` : r.overridden ? `${C.review}06` : "transparent" }}>
                    <td style={{ padding: "5px 9px" }}><input type="checkbox" checked={checked} onChange={() => toggle(r.recordId)} /></td>
                    <td style={{ padding: "5px 9px", whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 10, color: C.muted }}>{r.ro || "—"}{r.line ? ` · ${r.line}` : ""}</td>
                    <td style={{ padding: "5px 9px", maxWidth: 220 }}><div style={{ wordBreak: "break-word" }}>{r.correction || <em style={{ color: C.dim }}>{(r.cause || r.complaint || "blank").slice(0, 50)}</em>}</div></td>
                    <td style={{ padding: "5px 9px", whiteSpace: "nowrap" }}><span style={{ color: dc(r.predicted?.result) }}>{r.predicted?.result || "—"}</span>{r.overridden && <> <span style={{ color: C.dim }}>→</span> <b style={{ color: dc(d) }}>{d}</b></>}</td>
                    <td style={{ padding: "5px 9px", whiteSpace: "nowrap", fontSize: 10, color: r.confirmationTier === "auto" ? C.dim : C.learned }}>{r.confirmationTier}</td>
                    <td style={{ padding: "5px 9px" }}>{r.brainEligible ? <span style={{ color: C.learned, fontWeight: 700 }}>✓</span> : feedbackFor(r) ? <span title="Will upgrade with your feedback" style={{ color: C.accent, fontWeight: 700 }}>✎</span> : <span style={{ color: C.dim }}>—</span>}</td>
                    <td style={{ padding: "5px 9px", fontSize: 9, color: C.muted, whiteSpace: "nowrap" }}>{[r.signals?.partsPresent ? "🔧parts" : "", r.signals?.hoursJustifyRepair ? "⏱hrs" : "", r.signals?.hasRepairLanguage ? "R&R" : "", r.hasAnchorPart ? "⚙anchor" : ""].filter(Boolean).join(" ") || "—"}</td>
                    <td style={{ padding: "5px 9px", maxWidth: 220, color: C.muted }}>
                      <div style={{ wordBreak: "break-word" }}>{r.rationale?.text || ""}<span style={{ color: C.dim, fontSize: 9 }}> ({r.rationale?.source})</span></div>
                      {/* [CTO-3 2026-07-03] the upgrade ticket: feedback here turns a bulk/auto row into a promotable, human-rationaled precedent */}
                      {!r.brainEligible && (d === "Include" || d === "Exclude") && (
                        <input value={fb[r.recordId] || ""} onChange={e => setFb(p => ({ ...p, [r.recordId]: e.target.value }))} placeholder="feedback to upgrade → brain…"
                          style={{ marginTop: 3, width: "100%", boxSizing: "border-box", background: C.card2, border: `1px solid ${feedbackFor(r) ? C.accent : C.border2}`, borderRadius: 5, padding: "3px 6px", color: C.text, fontSize: 10, fontFamily: "inherit" }} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > 500 && <div style={{ padding: "6px 10px", fontSize: 10, color: C.review, borderTop: `1px solid ${C.border}` }}>Showing first 500 of {filtered.length} — narrow the filters to see the rest.</div>}
        {filtered.length === 0 && <div style={{ padding: 30, textAlign: "center", color: C.muted, fontSize: 13 }}>{total === 0 ? "No corpus yet — decisions will appear here as projects are classified and exported." : "No records match these filters."}</div>}
      </div>
    </div>
  );
}

// ── Confidence Dashboard — how well the automated decisions are doing ──────
// Ground truth = only human-VERIFIED lines (typed a decision, or ✓ confirmed). Accuracy is
// measured over those; "coverage" is what fraction of the corpus is verified. As review drops,
// keep a random sample flowing to review or these numbers go stale (acceptance sampling).
function ConfidenceTab({ allProjects = [] }) {
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [completedOnly, setCompletedOnly] = useState(true);
  const load = async () => { setLoading(true); setErr(""); try { setRecords(await fetchCorpus()); } catch (e) { setErr("Load error: " + e.message); setRecords([]); } setLoading(false); };
  useEffect(() => { load(); }, []);

  // Confidence is only meaningful on FINISHED projects — in-progress work would drag it down.
  const allProjEntries = allProjects.flatMap(u => u.projects || []);
  const completedIds = new Set(allProjEntries.filter(p => p.completed).map(p => p.projectId));
  const completedCount = completedIds.size;

  const pct = (n, d) => d ? Math.round((n / d) * 100) : null;
  const selStyle = { background: C.card3, color: C.text, border: `1px solid ${C.border2}`, borderRadius: 6, padding: "5px 8px", fontSize: 12, fontFamily: "inherit", cursor: "pointer" };

  if (loading) return <div style={{ color: C.muted, fontSize: 13 }}>Loading corpus…</div>;
  if (err) return <div style={{ color: T.color.exclude, fontSize: T.size.sm }}>⚠ {err} <Button variant="ghost" onClick={load}>Retry</Button></div>;

  const recs0 = records || [];
  const recs = completedOnly ? recs0.filter(r => completedIds.has(r.projectId)) : recs0;
  const total = recs.length;
  const finalDec = r => r.final?.humanDecision || r.final?.result;
  const isEval = r => (r.final?.humanDecision != null) || r.confirmationTier === "blessed"; // human verified
  const evals = recs.filter(isEval);
  const correct = evals.filter(r => !r.overridden);
  const acc = pct(correct.length, evals.length);

  const predIncl = evals.filter(r => r.predicted?.result === "Include");
  const predExcl = evals.filter(r => r.predicted?.result === "Exclude");
  const wrongIncl = predIncl.filter(r => finalDec(r) !== "Include"); // claimed something a human wouldn't — high severity
  const wrongExcl = predExcl.filter(r => finalDec(r) !== "Exclude"); // dropped something a human would keep

  const STAGES = [["rules", "Rules"], ["learned", "Learned"], ["validated", "Valid (AI-agreed)"], ["ai", "AI (fallback)"], ["cascade", "Cascade"], ["fallback", "Unclassified"]];
  const byStage = STAGES.map(([s, label]) => {
    const all = recs.filter(r => r.predicted?.stage === s);
    const ev = all.filter(isEval);
    const ok = ev.filter(r => !r.overridden);
    const wi = ev.filter(r => r.predicted?.result === "Include" && finalDec(r) !== "Include").length;
    const we = ev.filter(r => r.predicted?.result === "Exclude" && finalDec(r) !== "Exclude").length;
    return { s, label, total: all.length, ev: ev.length, acc: pct(ok.length, ev.length), wi, we };
  }).filter(x => x.total > 0);

  // AI validator: lines the AI flagged off a rule decision (→ Human Review)
  const aiFlags = recs.filter(r => r.pipelineSignals?.aiDisagree);
  const aiFlagsEval = aiFlags.filter(r => isEval(r) && r.pipelineSignals?.ruleDecision);
  const flagJustified = aiFlagsEval.filter(r => finalDec(r) !== r.pipelineSignals.ruleDecision); // rule really was wrong

  // System-routed Human Review (machine chose HR on its own)
  const sysHR = recs.filter(r => r.predicted?.result === "Human Review");
  const sysHREval = sysHR.filter(isEval);
  const hrToIncl = sysHREval.filter(r => finalDec(r) === "Include").length;
  const hrToExcl = sysHREval.filter(r => finalDec(r) === "Exclude").length;
  const hrStayed = sysHREval.length - hrToIncl - hrToExcl;

  // Calibration — does the AI's self-reported confidence match its actual verified accuracy?
  const CONF_BINS = [[0, 60, "< 60"], [60, 75, "60–74"], [75, 85, "75–84"], [85, 95, "85–94"], [95, 101, "95–100"]];
  const withConf = evals.filter(r => typeof r.aiConfidence === "number");
  const calib = CONF_BINS.map(([lo, hi, label]) => {
    const inBin = withConf.filter(r => r.aiConfidence >= lo && r.aiConfidence < hi);
    const ok = inBin.filter(r => !r.overridden);
    return { label, lo, hi, n: inBin.length, acc: pct(ok.length, inBin.length) };
  }).filter(x => x.n > 0);

  // [B3 2026-06-10] Auto-clear readiness — if the AI decided these unsupervised, how often would it match
  // the human, and at what confidence cutoff is that safe (esp. ZERO wrong-Includes)? The path to <100% review.
  const aiCallOf = r => {
    if (r.aiVerdict && (r.aiVerdict.result === "Include" || r.aiVerdict.result === "Exclude")) return { call: r.aiVerdict.result, conf: typeof r.aiVerdict.confidence === "number" ? r.aiVerdict.confidence : null };
    if (r.predicted?.stage === "ai" && (r.predicted.result === "Include" || r.predicted.result === "Exclude")) return { call: r.predicted.result, conf: typeof r.aiConfidence === "number" ? r.aiConfidence : null };
    return null;
  };
  const acPop = evals.map(r => { const a = aiCallOf(r); return a && a.conf != null ? { truth: finalDec(r), call: a.call, conf: a.conf } : null; }).filter(Boolean);
  const acRows = [60, 70, 75, 80, 85, 90, 95].map(t => {
    const at = acPop.filter(x => x.conf >= t);
    const agree = at.filter(x => x.call === x.truth).length;
    const wInc = at.filter(x => x.call === "Include" && x.truth !== "Include").length;
    const wExc = at.filter(x => x.call === "Exclude" && x.truth !== "Exclude").length;
    return { t, n: at.length, agree: pct(agree, at.length), wInc, wExc, cov: pct(at.length, acPop.length) };
  }).filter(x => x.n > 0);
  const acRec = acRows.find(x => x.agree != null && x.agree >= 98 && x.wInc === 0 && x.n >= 20);

  // [DASH 2026-06-11] Rules-vs-AI concordance. The all-lines pass attaches an independent AI verdict
  // (r.aiVerdict) to every rule/learned decision + whether it agreed (r.aiAgrees) — observational, never
  // changed the call. Shows how often the two engines concur, per stage, and — when they split on a
  // human-verified line — which one the human sided with.
  const RVAI_STAGES = [["rules", "Rules"], ["learned", "Learned"], ["validated", "Valid (AI-agreed)"]];
  const rvaiPop = recs.filter(r => typeof r.aiAgrees === "boolean");
  const rvaiAgree = rvaiPop.filter(r => r.aiAgrees).length;
  const rvaiRate = pct(rvaiAgree, rvaiPop.length);
  const rvaiByStage = RVAI_STAGES.map(([s, label]) => {
    const all = rvaiPop.filter(r => r.predicted?.stage === s);
    const ag = all.filter(r => r.aiAgrees).length;
    return { s, label, n: all.length, agree: pct(ag, all.length), dis: all.length - ag };
  }).filter(x => x.n > 0);
  const rvaiDisEval = rvaiPop.filter(r => !r.aiAgrees && isEval(r));
  const aiWon = rvaiDisEval.filter(r => r.aiVerdict && finalDec(r) === r.aiVerdict.result && finalDec(r) !== r.predicted?.result).length;
  const ruleWon = rvaiDisEval.filter(r => finalDec(r) === r.predicted?.result).length;
  const rvaiNeither = rvaiDisEval.length - aiWon - ruleWon;

  const Card = ({ label, value, sub, color }) => (
    <div style={{ background: T.color.surface, border: `1px solid ${T.color.border}`, borderRadius: T.radius.lg, padding: "14px 16px", minWidth: 150, boxShadow: T.shadow.e1 }}>
      <div style={{ fontSize: T.size.micro, color: T.color.textMuted, textTransform: "uppercase", letterSpacing: T.tracking.caps, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: T.size.stat, fontWeight: T.weight.bold, color: color || T.color.text, letterSpacing: T.tracking.tightNeg }}>{value}</div>
      {sub && <div style={{ fontSize: T.size.micro, color: T.color.textFaint, marginTop: 3 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, color: C.muted }}>Ground truth = human-verified lines only ({evals.length} of {total}, {pct(evals.length, total) ?? 0}% coverage). Accuracy is meaningless on lines no one checked.</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 5, color: C.muted, fontSize: 12, cursor: "pointer" }}><input type="checkbox" checked={completedOnly} onChange={e => setCompletedOnly(e.target.checked)} /> completed projects only ({completedCount})</label>
          <Button variant="ghost" onClick={load}>↺ Reload</Button>
        </div>
      </div>
      {completedOnly && completedCount === 0 && <div style={{ background: `${C.review}10`, border: `1px solid ${C.review}40`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: C.review, marginBottom: 14 }}>No projects marked complete yet. Open a project in the classifier and hit <b>✓ Mark complete</b> when you've finished reviewing it — or untick the box above to include in-progress projects.</div>}

      {total === 0 ? <div style={{ padding: 40, textAlign: "center", color: C.muted }}>No corpus yet — classify and export/leave a project to start collecting.</div> : <>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
          <Card label="Verified accuracy" value={acc == null ? "—" : `${acc}%`} sub={`${correct.length}/${evals.length} agreed`} color={acc >= 90 ? C.include : acc >= 75 ? C.review : C.exclude} />
          <Card label="⚠ Wrong-Include rate" value={pct(wrongIncl.length, predIncl.length) == null ? "—" : `${pct(wrongIncl.length, predIncl.length)}%`} sub={`${wrongIncl.length}/${predIncl.length} · OEM finds these easily`} color={wrongIncl.length === 0 ? C.include : C.exclude} />
          <Card label="Wrong-Exclude rate" value={pct(wrongExcl.length, predExcl.length) == null ? "—" : `${pct(wrongExcl.length, predExcl.length)}%`} sub={`${wrongExcl.length}/${predExcl.length} · lost qualifying lines`} color={wrongExcl.length === 0 ? C.include : C.review} />
          <Card label="Auto-decided" value={`${recs.filter(r => r.predicted?.result !== "Human Review").length}`} sub={`${sysHR.length} sent to review`} color={C.accent} />
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>Accuracy by stage (the "Via" column)</div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: C.card2 }}>{["Stage", "Total", "Verified", "Accuracy", "Wrong-Incl", "Wrong-Excl"].map((h, i) => <th key={i} style={{ padding: "8px 12px", textAlign: i === 0 ? "left" : "right", fontSize: 9, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>)}</tr></thead>
            <tbody>
              {byStage.map(x => (
                <tr key={x.s} style={{ borderTop: `1px solid ${C.border2}` }}>
                  <td style={{ padding: "7px 12px", color: x.s === "ai" ? C.accent : C.text, fontWeight: x.s === "ai" ? 700 : 400 }}>{x.label}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", color: C.muted }}>{x.total}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", color: C.muted }}>{x.ev}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", fontWeight: 700, color: x.acc == null ? C.dim : x.acc >= 90 ? C.include : x.acc >= 75 ? C.review : C.exclude }}>{x.acc == null ? "—" : `${x.acc}%`}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", color: x.wi ? C.exclude : C.dim }}>{x.wi}</td>
                  <td style={{ padding: "7px 12px", textAlign: "right", color: x.we ? C.review : C.dim }}>{x.we}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>AI confidence calibration</div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
          {withConf.length === 0
            ? <div style={{ fontSize: 12, color: C.muted }}>No AI-confidence data yet. The AI now reports a 0–100 confidence per decision — lines classified <i>after this deploy</i>, once verified, will populate this.</div>
            : <>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>Of the lines the AI rated in each band, how many it actually got right (on {withConf.length} verified AI/validated lines). Well-calibrated = accuracy ≈ the band. If a high band underperforms, the AI is overconfident there; if a band is consistently ~100%, it's a safe auto-clear candidate.</div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ background: C.card2 }}>{["AI confidence", "Verified lines", "Actual accuracy", ""].map((h, i) => <th key={i} style={{ padding: "6px 12px", textAlign: i === 0 ? "left" : i === 3 ? "left" : "right", fontSize: 9, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>)}</tr></thead>
                <tbody>{calib.map(b => {
                  const mid = (b.lo + Math.min(b.hi, 100)) / 2; const gap = b.acc == null ? null : b.acc - mid;
                  return (
                    <tr key={b.label} style={{ borderTop: `1px solid ${C.border2}` }}>
                      <td style={{ padding: "6px 12px", color: C.text }}>{b.label}</td>
                      <td style={{ padding: "6px 12px", textAlign: "right", color: C.muted }}>{b.n}</td>
                      <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 700, color: b.acc == null ? C.dim : b.acc >= 90 ? C.include : b.acc >= 75 ? C.review : C.exclude }}>{b.acc == null ? "—" : `${b.acc}%`}</td>
                      <td style={{ padding: "6px 12px", fontSize: 10, color: gap == null ? C.dim : gap < -10 ? C.exclude : gap > 10 ? C.accent : C.muted }}>{gap == null ? "" : gap < -10 ? "overconfident" : gap > 10 ? "underconfident" : "well-calibrated"}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </>}
        </div>

        {/* [B3 2026-06-10] Auto-clear readiness */}
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>🎯 Auto-clear readiness</div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
          {acPop.length === 0
            ? <div style={{ fontSize: 12, color: C.muted }}>No data yet. Once lines classified after the brain deploy are human-verified, this shows — for each AI-confidence cutoff — how often the AI's unsupervised call would have matched the human, so you can pick a safe auto-clear threshold.</div>
            : <>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>If the AI decided these <b>{acPop.length}</b> verified lines unsupervised: at each confidence cutoff, how often it matches the human — and how many <b>wrong-Includes</b> slip through (the error the OEM finds). Pick the lowest cutoff that's safe.</div>
              {acRec
                ? <div style={{ background: `${C.include}12`, border: `1px solid ${C.include}40`, borderRadius: 8, padding: "10px 12px", marginBottom: 12, fontSize: 12, color: C.include }}>✓ Safe auto-clear candidate: confidence <b>≥ {acRec.t}</b> — {acRec.agree}% agree with humans, <b>0 wrong-Includes</b>, and {acRec.cov}% of AI-decided lines ({acRec.n}) would clear without review.</div>
                : <div style={{ background: `${C.review}12`, border: `1px solid ${C.review}40`, borderRadius: 8, padding: "10px 12px", marginBottom: 12, fontSize: 12, color: C.review }}>No cutoff yet clears the bar (≥98% agree, 0 wrong-Includes, ≥20 lines). Keep reviewing + approving — the live brain (A1) should lift this over time.</div>}
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr style={{ background: C.card2 }}>{["Confidence ≥", "AI-decided lines", "Agree w/ human", "Wrong-Incl", "Wrong-Excl", "Would auto-clear"].map((h, i) => <th key={i} style={{ padding: "6px 12px", textAlign: i === 0 ? "left" : "right", fontSize: 9, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>)}</tr></thead>
                <tbody>{acRows.map(x => (
                  <tr key={x.t} style={{ borderTop: `1px solid ${C.border2}`, background: acRec && x.t === acRec.t ? `${C.include}08` : "transparent" }}>
                    <td style={{ padding: "6px 12px", color: C.text, fontWeight: 700 }}>{x.t}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", color: C.muted }}>{x.n}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 700, color: x.agree == null ? C.dim : x.agree >= 98 ? C.include : x.agree >= 90 ? C.review : C.exclude }}>{x.agree == null ? "—" : `${x.agree}%`}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", color: x.wInc ? C.exclude : C.dim }}>{x.wInc}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", color: x.wExc ? C.review : C.dim }}>{x.wExc}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", color: C.muted }}>{x.cov == null ? "—" : `${x.cov}%`}</td>
                  </tr>
                ))}</tbody>
              </table>
            </>}
        </div>

        {/* [DASH 2026-06-11] Rules vs AI agreement */}
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>🤝 Rules vs AI agreement</div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
          {rvaiPop.length === 0
            ? <div style={{ fontSize: 12, color: C.muted }}>No data yet. The all-lines pass attaches an independent AI verdict to every rule/learned decision (observational); once lines classified after that deploy accrue, this shows how often the two engines concur.</div>
            : <>
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>On <b>{rvaiPop.length}</b> rule/learned decisions, the AI independently re-judged the line (observational — it never changed the decision). How often the engines concur, and — when they split on a human-verified line — who the human sided with.</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                <Card label="Overall agreement" value={rvaiRate == null ? "—" : `${rvaiRate}%`} sub={`${rvaiAgree}/${rvaiPop.length} concur`} color={rvaiRate == null ? C.dim : rvaiRate >= 90 ? C.include : rvaiRate >= 75 ? C.review : C.exclude} />
                <Card label="Disagreements" value={`${rvaiPop.length - rvaiAgree}`} sub={`${rvaiDisEval.length} since verified`} color={C.accent} />
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: rvaiDisEval.length ? 4 : 0 }}>
                <thead><tr style={{ background: C.card2 }}>{["Stage", "Decisions", "AI agreed", "Disagreed"].map((h, i) => <th key={i} style={{ padding: "6px 12px", textAlign: i === 0 ? "left" : "right", fontSize: 9, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px" }}>{h}</th>)}</tr></thead>
                <tbody>{rvaiByStage.map(x => (
                  <tr key={x.s} style={{ borderTop: `1px solid ${C.border2}` }}>
                    <td style={{ padding: "6px 12px", color: C.text }}>{x.label}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", color: C.muted }}>{x.n}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 700, color: x.agree == null ? C.dim : x.agree >= 90 ? C.include : x.agree >= 75 ? C.review : C.exclude }}>{x.agree == null ? "—" : `${x.agree}%`}</td>
                    <td style={{ padding: "6px 12px", textAlign: "right", color: x.dis ? C.review : C.dim }}>{x.dis}</td>
                  </tr>
                ))}</tbody>
              </table>
              {rvaiDisEval.length > 0 && <div style={{ marginTop: 10, fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
                On the <b style={{ color: C.text }}>{rvaiDisEval.length}</b> verified splits: <b style={{ color: C.accent }}>AI was right {pct(aiWon, rvaiDisEval.length) ?? "—"}{pct(aiWon, rvaiDisEval.length) != null ? "%" : ""}</b> ({aiWon}) · <b style={{ color: C.include }}>rule was right {pct(ruleWon, rvaiDisEval.length) ?? "—"}{pct(ruleWon, rvaiDisEval.length) != null ? "%" : ""}</b> ({ruleWon}) · <span style={{ color: C.dim }}>neither {rvaiNeither}</span>.<br />
                <span style={{ fontSize: 11, color: C.dim }}>If the AI wins most splits, lean on it more; if the rules win, the AI's disagreements on those stages are mostly noise.</span>
              </div>}
            </>}
        </div>

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 280, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>🤖 AI validator (the HR-maker)</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
              Flagged <b style={{ color: C.text }}>{aiFlags.length}</b> rule decisions to Human Review.<br />
              Of {aiFlagsEval.length} verified flags, the rule really was wrong <b style={{ color: C.learned }}>{pct(flagJustified.length, aiFlagsEval.length) ?? "—"}{pct(flagJustified.length, aiFlagsEval.length) != null ? "%" : ""}</b> of the time (validator precision).<br />
              <span style={{ fontSize: 11, color: C.dim }}>Low precision → the AI is over-flagging and making needless HR work. High → its flags are trustworthy.</span>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 280, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>Human Review routing</div>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.7 }}>
              System sent <b style={{ color: C.text }}>{sysHR.length}</b> lines to review ({sysHREval.length} since verified).<br />
              Those resolved to: <b style={{ color: C.include }}>{hrToIncl} Include</b> · <b style={{ color: C.exclude }}>{hrToExcl} Exclude</b> · <span style={{ color: C.review }}>{hrStayed} stayed</span>.<br />
              <span style={{ fontSize: 11, color: C.dim }}>If most HR resolves one obvious way, that slice is a candidate to start auto-deciding.</span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 16, fontSize: 11, color: C.dim, lineHeight: 1.6 }}>
          ⚠ These rates only count lines a human actually verified. As you stop reviewing the easy ones, keep a random sample flowing to review or accuracy here goes stale. Per-line AI confidence + an independent AI verdict are now collected (the all-lines understanding pass) → the calibration + auto-clear tables populate as verified lines accrue.
        </div>
      </>}
    </div>
  );
}

// ── Main admin panel ──────────────────────────────────────────────────────
function AdminPanel() {
  const [submissions, setSubmissions] = useState({});
  const [masterDb,    setMasterDb]    = useState({ [DB1_KEY]: [], [DB2_KEY]: [], [DB3_KEY]: [] });
  const [loading,     setLoading]     = useState(true);
  const [msg,         setMsg]         = useState("");
  const [activeTab,   setActiveTab]   = useState("submissions");
  const [batchLoading, setBatchLoading] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  // [SUBMISSIONS-REDESIGN 2026-06-16] search / filter / sort for the submissions feed
  const [subSearch, setSubSearch] = useState("");
  const [subDecision, setSubDecision] = useState("all");   // all | Include | Exclude | Human Review | assoc
  const [subUser, setSubUser] = useState("all");
  const [subSort, setSubSort] = useState("newest");        // newest | oldest
  const [subSel, setSubSel] = useState(() => new Set());   // selection keys `username|dbKey|id`

  const load = async () => {
    setLoading(true);
    try {
      const { keys } = await apiFetch("list-submissions");
      const queues = {};
      for (const key of (keys || [])) {
        const { value } = await apiGet(key);
        if (value?.length) queues[key] = value;
      }
      setSubmissions(queues);
      const [d1, d2, d3] = await Promise.all([apiGet(DB1_KEY), apiGet(DB2_KEY), apiGet(DB3_KEY)]);
      setMasterDb({ [DB1_KEY]: d1.value || [], [DB2_KEY]: d2.value || [], [DB3_KEY]: d3.value || [] });
      try { const ap = await apiFetch("list-all-sessions"); setAllProjects(ap.value || []); } catch { /* older backend without the action */ }
    } catch (e) { setMsg("Load error: " + e.message); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const flashMsg = (text) => { setMsg(text); setTimeout(() => setMsg(""), 6000); };

  // [SUBMISSIONS-REDESIGN 2026-06-16] optimistic local updates — drop the approved/rejected entries from
  // state in place instead of re-fetching everything (no more full-page reload on every action).
  const removeFromQueues = (removals) => {  // removals: [{username, dbKey, id}]
    setSubmissions(prev => {
      const next = { ...prev };
      for (const { username, dbKey, id } of removals) {
        const qk = `submissions:${username}:${dbKey}`;
        if (!next[qk]) continue;
        const filtered = next[qk].filter(e => e.id !== id);
        if (filtered.length) next[qk] = filtered; else delete next[qk];
      }
      return next;
    });
    setSubSel(prev => { const n = new Set(prev); for (const { username, dbKey, id } of removals) n.delete(`${username}|${dbKey}|${id}`); return n; });
  };

  const handleApprove = async (entry, dbKey, username) => {
    try {
      if (dbKey === OEM_MAPPINGS_KEY) {
        await apiFetch("approve-oem-mappings", { entries:[entry], queueKey:`submissions:${username}:${dbKey}` });
        removeFromQueues([{username,dbKey,id:entry.id}]); flashMsg("✓ OEM mapping approved — appended to the versioned ledger"); return;
      }
      const { masterEntries, brainOnlyEntries } = partitionPromotion(dbKey, [entry]);
      if (masterEntries.length) await apiFetch("approve", { masterKey: dbKey, entries: masterEntries, queueKey: `submissions:${username}:${dbKey}` });
      const bf = await feedBrainEntries(dbKey, [entry]);  // [A1] approve-gated brain feed
      // [WPF1-R1] A brain-only entry's ONLY durable home is the brain — so it leaves the queue
      // only after a clean feed. On feed failure it stays queued for a retry (master entries are
      // already durable in DB1, so their queue removal above is safe either way).
      const keepQueued = brainOnlyEntries.length > 0 && bf.failed > 0;
      if (brainOnlyEntries.length && !keepQueued) await apiFetch("reject", { queueKey: `submissions:${username}:${dbKey}`, entryIds: brainOnlyEntries.map(e => e.id) });
      setMasterDb(prev => ({ ...prev, [dbKey]: [...(prev[dbKey] || []), ...masterEntries] }));
      if (!keepQueued) removeFromQueues([{ username, dbKey, id: entry.id }]);
      flashMsg(keepQueued
        ? `⚠ Brain feed failed — blank-correction entry KEPT IN QUEUE for retry${brainFeedWarn(bf)}`
        : `✓ Approved — ${masterEntries.length ? "promoted to master DB + brain" : "promoted to brain only"}${brainOnlyNote(brainOnlyEntries.length)}${brainFeedWarn(bf)}`);
    } catch (e) { flashMsg("Error: " + e.message); }
  };

  const handleReject = async (entry, dbKey, username) => {
    try {
      await apiFetch("reject", { queueKey: `submissions:${username}:${dbKey}`, entryIds: [entry.id] });
      removeFromQueues([{ username, dbKey, id: entry.id }]);
      flashMsg("✗ Rejected — removed from queue");
    } catch (e) { flashMsg("Error: " + e.message); }
  };

  // [SUBMISSIONS-REDESIGN 2026-06-16] multi-select approve/reject — group the checked entries by
  // (user, dbKey), batch one server call per group, then optimistically drop them. Works on the
  // full selection (subSelResolved), so it survives filter/search changes.
  const handleApproveSelected = async () => {
    const sel = subSelResolved;
    if (!sel.length || !window.confirm(`Approve ${sel.length} selected submission(s)? Promotes to master + brain.`)) return;
    setBatchLoading(true);
    try {
      const groups = {};
      for (const s of sel) { const k = `${s.username}|${s.dbKey}`; (groups[k] = groups[k] || []).push(s); }
      const removals = []; let bfFailed = 0; let brainOnlyCount = 0; let keptQueued = 0;
      for (const items of Object.values(groups)) {
        const { username, dbKey } = items[0]; const entries = items.map(i => i.entry);
        if (dbKey === OEM_MAPPINGS_KEY) {
          await apiFetch("approve-oem-mappings", {entries,queueKey:`submissions:${username}:${dbKey}`});
          for (const e of entries) removals.push({username,dbKey,id:e.id});
          continue;
        }
        const { masterEntries, brainOnlyEntries } = partitionPromotion(dbKey, entries); brainOnlyCount += brainOnlyEntries.length;
        if (masterEntries.length) await apiFetch("approve", { masterKey: dbKey, entries: masterEntries, queueKey: `submissions:${username}:${dbKey}` });
        const bf = await feedBrainEntries(dbKey, entries); bfFailed += bf.failed;
        // [WPF1-R1] Brain-only entries leave the queue only after a clean feed (see handleApprove).
        const keepBrainOnly = brainOnlyEntries.length > 0 && bf.failed > 0;
        if (brainOnlyEntries.length && !keepBrainOnly) await apiFetch("reject", { queueKey: `submissions:${username}:${dbKey}`, entryIds: brainOnlyEntries.map(e => e.id) });
        if (keepBrainOnly) keptQueued += brainOnlyEntries.length;
        setMasterDb(prev => ({ ...prev, [dbKey]: [...(prev[dbKey] || []), ...masterEntries] }));
        const keptIds = keepBrainOnly ? new Set(brainOnlyEntries.map(e => e.id)) : new Set();
        for (const e of entries) if (!keptIds.has(e.id)) removals.push({ username, dbKey, id: e.id });
      }
      removeFromQueues(removals);
      flashMsg(`✓ Approved ${sel.length} selected — promoted to master + brain${brainOnlyNote(brainOnlyCount)}${keptQueued ? ` — ⚠ ${keptQueued} blank-correction entr${keptQueued === 1 ? "y" : "ies"} KEPT IN QUEUE (brain feed failed)` : ""}${brainFeedWarn({ failed: bfFailed })}`);
    } catch (e) { flashMsg("Error: " + e.message); }
    setBatchLoading(false);
  };

  const handleRejectSelected = async () => {
    const sel = subSelResolved;
    if (!sel.length || !window.confirm(`Reject ${sel.length} selected submission(s)? Cannot be undone.`)) return;
    setBatchLoading(true);
    try {
      const groups = {};
      for (const s of sel) { const k = `${s.username}|${s.dbKey}`; (groups[k] = groups[k] || []).push(s); }
      const removals = [];
      for (const items of Object.values(groups)) {
        const { username, dbKey } = items[0]; const ids = items.map(i => i.entry.id);
        await apiFetch("reject", { queueKey: `submissions:${username}:${dbKey}`, entryIds: ids });
        for (const id of ids) removals.push({ username, dbKey, id });
      }
      removeFromQueues(removals);
      flashMsg(`✗ Rejected ${sel.length} selected`);
    } catch (e) { flashMsg("Error: " + e.message); }
    setBatchLoading(false);
  };

  // [AUDIT 2026-06-10] Delete a master entry at the SOURCE (DB1/DB2/DB3) AND its derived brain copy,
  // so a bad precedent stays gone even through a re-backfill (backfill re-derives the brain from DB1).
  const deleteMasterEntry = async (key, entry) => {
    if (!window.confirm("Delete this master entry AND its brain copy? Removes it at the source so Backfill can't restore it.")) return;
    try {
      await apiFetch("delete-entry", { key, entryId: entry.id });
      if (key === DB1_KEY) await brainPost("delete", { type: "precedent", id: `L1|${(entry.correction || "").toLowerCase().trim()}|${entry.decision}` });
      else if (key === DB2_KEY) await brainPost("delete", { type: "association", id: `L2|${(entry.associateCorr || "").toLowerCase().trim()}|${(entry.anchorCorr || "").toLowerCase().trim()}` });
      setMasterDb(prev => ({ ...prev, [key]: (prev[key] || []).filter(x => x.id !== entry.id) }));
      flashMsg("✓ Deleted from master + brain (won't return on backfill)");
    } catch (e) { flashMsg("Delete error: " + e.message); }
  };

  // ── Batch operations (Item 24 — client-side merge) ───────────────────
  // [SIGNAL-EDIT 2026-07-20] Edit a submission's learning signal at the approval gate — the
  // feedback-writer quality bar lives HERE now: reviewers write raw whys; admin tightens the
  // keepers into brain-grade signals before approving. Updates the entry inside the client
  // `submissions` state, so EVERY approve path (single, bulk-selected, batch-user, batch-all —
  // they all read from this state) carries the edited text into DB1/DB2 and the brain feed.
  // Client-side only: edits live until page reload — edit, then approve.
  const handleEditSignal = (targetUsername, dbKey, entryId, text) => {
    const t = String(text || "").trim();
    if (!t) return;
    const qKey = `submissions:${targetUsername}:${dbKey}`;
    setSubmissions(prev => {
      const q = prev[qKey]; if (!q) return prev;
      return { ...prev, [qKey]: q.map(e => e.id === entryId ? { ...e, learningSignal: t, reasoning: t, _signalEdited: true } : e) };
    });
  };

  const handleBatchApproveUser = async (username) => {
    if (!window.confirm(`Approve ALL submissions from ${username}? Duplicates auto-removed.`)) return;
    setBatchLoading(true);
    try {
      // Client-side merge: collect entries from this user's queues
      const userQueues = Object.entries(submissions).filter(([k]) => k.startsWith(`submissions:${username}:`));
      const byMaster = {};
      for (const [qKey, entries] of userQueues) {
        const masterKey = qKey.replace(`submissions:${username}:`, "");
        if (!byMaster[masterKey]) byMaster[masterKey] = { entries: [], queueKeys: [] };
        byMaster[masterKey].entries.push(...entries);
        byMaster[masterKey].queueKeys.push(qKey);
      }
      let totalMerged = 0; let bfFailed = 0; let brainOnlyCount = 0; let queuesKept = 0;
      for (const [masterKey, { entries, queueKeys }] of Object.entries(byMaster)) {
        const master = masterDb[masterKey] || [];
        const masterIds = new Set(master.map(e => e.id));
        const seen = new Set();
        const unique = entries.filter(e => { if (!e.id || masterIds.has(e.id) || seen.has(e.id)) return false; seen.add(e.id); return true; });
        const { masterEntries: toAdd, brainOnlyEntries } = partitionPromotion(masterKey, unique); brainOnlyCount += brainOnlyEntries.length;
        if (toAdd.length > 0) {
          await apiFetch("set-master", { key: masterKey, data: [...master, ...toAdd] });
        }
        const bf = await feedBrainEntries(masterKey, unique); bfFailed += bf.failed;  // [A1] approve-gated brain feed
        // [WPF1-R1] Brain-only entries' ONLY durable home is the brain — if the feed failed and
        // this master had brain-only entries, KEEP the queues so re-approve can retry (approve is
        // id-deduped, so nothing double-merges). Otherwise clear as before.
        if (brainOnlyEntries.length > 0 && bf.failed > 0) { queuesKept += queueKeys.length; }
        else { for (const qKey of queueKeys) { await apiFetch("set-master", { key: qKey, data: [] }); } }
        totalMerged += toAdd.length;
      }
      flashMsg(`✓ Batch approved — ${totalMerged} unique entries merged${brainOnlyNote(brainOnlyCount)}${queuesKept ? ` — ⚠ ${queuesKept} queue(s) KEPT for retry (brain feed failed with blank-correction entries)` : ""}${brainFeedWarn({ failed: bfFailed })}`);
      await load();
    } catch (e) { flashMsg("Batch approve error: " + e.message); }
    setBatchLoading(false);
  };

  const handleBatchRejectUser = async (username) => {
    if (!window.confirm(`Reject ALL submissions from ${username}? Cannot be undone.`)) return;
    setBatchLoading(true);
    try {
      const userQueues = Object.keys(submissions).filter(k => k.startsWith(`submissions:${username}:`));
      for (const qKey of userQueues) { await apiFetch("set-master", { key: qKey, data: [] }); }
      flashMsg("✗ All submissions rejected");
      await load();
    } catch (e) { flashMsg("Batch reject error: " + e.message); }
    setBatchLoading(false);
  };

  const handleBatchApproveAll = async () => {
    if (!window.confirm("Approve ALL submissions from ALL users? Duplicates auto-removed.")) return;
    setBatchLoading(true);
    try {
      const byMaster = {};
      for (const [qKey, entries] of Object.entries(submissions)) {
        const parts = qKey.replace("submissions:", "").split(":");
        const masterKey = parts.slice(1).join(":");
        if (!byMaster[masterKey]) byMaster[masterKey] = { entries: [], queueKeys: [] };
        byMaster[masterKey].entries.push(...entries);
        byMaster[masterKey].queueKeys.push(qKey);
      }
      let totalMerged = 0; let bfFailed = 0; let brainOnlyCount = 0; let queuesKept = 0;
      for (const [masterKey, { entries, queueKeys }] of Object.entries(byMaster)) {
        const master = masterDb[masterKey] || [];
        const masterIds = new Set(master.map(e => e.id));
        const seen = new Set();
        const unique = entries.filter(e => { if (!e.id || masterIds.has(e.id) || seen.has(e.id)) return false; seen.add(e.id); return true; });
        const { masterEntries: toAdd, brainOnlyEntries } = partitionPromotion(masterKey, unique); brainOnlyCount += brainOnlyEntries.length;
        if (toAdd.length > 0) {
          await apiFetch("set-master", { key: masterKey, data: [...master, ...toAdd] });
        }
        const bf = await feedBrainEntries(masterKey, unique); bfFailed += bf.failed;  // [A1] approve-gated brain feed
        // [WPF1-R1] Same queue-retention rule as handleBatchApproveUser.
        if (brainOnlyEntries.length > 0 && bf.failed > 0) { queuesKept += queueKeys.length; }
        else { for (const qKey of queueKeys) { await apiFetch("set-master", { key: qKey, data: [] }); } }
        totalMerged += toAdd.length;
      }
      flashMsg(`✓ Batch approved all — ${totalMerged} unique entries merged${brainOnlyNote(brainOnlyCount)}${queuesKept ? ` — ⚠ ${queuesKept} queue(s) KEPT for retry (brain feed failed with blank-correction entries)` : ""}${brainFeedWarn({ failed: bfFailed })}`);
      await load();
    } catch (e) { flashMsg("Batch approve error: " + e.message); }
    setBatchLoading(false);
  };

  const handleBatchRejectAll = async () => {
    if (!window.confirm("Reject ALL submissions? Cannot be undone.")) return;
    setBatchLoading(true);
    try {
      for (const qKey of Object.keys(submissions)) { await apiFetch("set-master", { key: qKey, data: [] }); }
      flashMsg("✗ All submissions rejected");
      await load();
    } catch (e) { flashMsg("Batch reject error: " + e.message); }
    setBatchLoading(false);
  };

  const parseQueueKey = (key) => {
    const parts = key.replace("submissions:", "").split(":");
    const username = parts[0];
    const dbKey = parts.slice(1).join(":");
    return { username, dbKey };
  };

  const byUser = {};
  for (const [qKey, entries] of Object.entries(submissions)) {
    const { username, dbKey } = parseQueueKey(qKey);
    if (!byUser[username]) byUser[username] = [];
    byUser[username].push({ dbKey, qKey, entries });
  }

  const totalPending = Object.values(submissions).reduce((n, e) => n + e.length, 0);
  const dbLabel = { [DB1_KEY]: "DB1 Flow 1", [DB2_KEY]: "DB2 Associations", [DB3_KEY]: "DB3 Flow 1" };

  // [SUBMISSIONS-REDESIGN 2026-06-16] flatten every queue into one searchable, sortable feed —
  // replaces the user→db grouping that capped each group at 10 (hiding most entries).
  const subUsers = Object.keys(byUser).sort();
  const subFlatAll = [];
  for (const [qKey, entries] of Object.entries(submissions)) {
    const { username, dbKey } = parseQueueKey(qKey);
    for (const entry of entries) subFlatAll.push({ entry, dbKey, username });
  }
  const subQ = subSearch.trim().toLowerCase();
  const subFiltered = subFlatAll.filter(({ entry, dbKey, username }) => {
    if (subUser !== "all" && username !== subUser) return false;
    if (subDecision === "assoc") { if (dbKey !== DB2_KEY) return false; }
    else if (subDecision !== "all" && entry.decision !== subDecision) return false;
    if (subQ) {
      const hay = [entry.ro, entry.line, entry.correction, entry.cause, entry.complaint, entry.learningSignal, entry.reasoning, entry.associateCorr, entry.anchorCorr, username]
        .filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(subQ)) return false;
    }
    return true;
  });
  subFiltered.sort((a, b) => {
    const da = new Date(a.entry.date || 0).getTime() || 0, db = new Date(b.entry.date || 0).getTime() || 0;
    return subSort === "newest" ? db - da : da - db;
  });
  const subScopeCount = subUser === "all" ? totalPending : (byUser[subUser] || []).reduce((n, q) => n + q.entries.length, 0);
  const selKey = (username, dbKey, id) => `${username}|${dbKey}|${id}`;
  const subSelResolved = subFlatAll.filter(({ entry, dbKey, username }) => subSel.has(selKey(username, dbKey, entry.id)));
  const toggleSel = (k) => setSubSel(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const selectAllShown = () => setSubSel(prev => { const n = new Set(prev); for (const { entry, dbKey, username } of subFiltered) n.add(selKey(username, dbKey, entry.id)); return n; });
  const shownAllSelected = subFiltered.length > 0 && subFiltered.every(({ entry, dbKey, username }) => subSel.has(selKey(username, dbKey, entry.id)));

  return (
    <div style={{ minHeight: "100vh", background: T.color.bgImage, backgroundAttachment: "fixed", color: T.color.text, fontFamily: T.font.sans }}>
      {/* [D8 2026-06-10] kit-styled admin shell — matches the Brain console + design chrome */}
      <header style={{ display: "flex", alignItems: "center", gap: T.space[6], padding: `0 ${T.space[7]}px`, height: 60, borderBottom: `1px solid ${T.color.border}`, background: T.color.surface, position: "sticky", top: 0, zIndex: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: `linear-gradient(160deg, ${T.color.accent}, ${T.color.accentActive})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, boxShadow: T.shadow.e2 }}>{"⚖︎"}</div>
          <div>
            <div style={{ fontSize: T.size.base, fontWeight: T.weight.semibold, color: T.color.text, letterSpacing: T.tracking.tightNeg, lineHeight: 1.1 }}>EZ Wins Admin</div>
            <div style={{ fontSize: T.size.caption, color: T.color.textMuted }}>Submission review · brain operations</div>
          </div>
        </div>
        {msg && <div style={{ marginLeft: 8, fontSize: T.size.sm, fontWeight: T.weight.semibold, color: msg.startsWith("✓") ? T.color.include : msg.startsWith("✗") ? T.color.exclude : T.color.review }}>{msg}</div>}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: T.space[4] }}>
          <HealthChip />{/* [WPB1 2026-08-04] AI / Brain / Embeddings — a dead key announces itself */}
          <Button variant="ghost" onClick={load} disabled={batchLoading}>↺ Refresh</Button>
        </div>
      </header>

      <div style={{ padding: `${T.space[5]}px ${T.space[7]}px 0`, background: T.color.surface, borderBottom: `1px solid ${T.color.border}` }}>
        <Tabs value={activeTab} onChange={setActiveTab} tabs={[
          { id: "submissions", label: "Submissions", count: totalPending },
          { id: "master", label: "Master DB", count: Object.values(masterDb).reduce((n, a) => n + a.length, 0) },
          { id: "projects", label: "Projects", count: allProjects.reduce((n, u) => n + (u.projects?.length || 0), 0) },
          { id: "corpus", label: "🧠 Corpus" },
          { id: "confidence", label: "📊 Confidence" },
          { id: "brain", label: "✨ Brain" },
          { id: "users", label: "👤 Users" },
          { id: "seed", label: "⬆ Seed KB" },
        ]} />
      </div>

      <div style={{ padding: `${T.space[7]}px`, maxWidth: 1000, margin: "0 auto" }}>
        {loading && <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>}
        {batchLoading && <div style={{ color: C.review, fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Processing batch operation… do not close this tab.</div>}

        {/* [SUBMISSIONS-REDESIGN 2026-06-16] searchable, sortable, newest-first feed — no per-group cap */}
        {!loading && activeTab === "submissions" && (
          <>
            {totalPending === 0 ? (
              <div style={{ color: T.color.textMuted, fontSize: 13, textAlign: "center", marginTop: 80 }}>No pending submissions. All clear ✓</div>
            ) : (
              <>
                {/* Toolbar: search · user · sort · decision chips · counts · batch */}
                <Card style={{ marginBottom: T.space[5], padding: T.space[4] }}>
                  <div style={{ display: "flex", gap: T.space[3], alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 300px", minWidth: 200 }}>
                      <Input value={subSearch} onChange={e => setSubSearch(e.target.value)} placeholder="Search RO, correction, cause, reasoning, user…" leftIcon="⌕" />
                    </div>
                    <Select value={subUser} onChange={e => setSubUser(e.target.value)}
                      options={[{ value: "all", label: `All users (${subUsers.length})` }, ...subUsers.map(u => ({ value: u, label: u }))]} style={{ width: 180 }} />
                    <Button variant="ghost" onClick={() => setSubSort(s => s === "newest" ? "oldest" : "newest")} title="Toggle sort order">
                      {subSort === "newest" ? "↓ Newest first" : "↑ Oldest first"}
                    </Button>
                  </div>
                  <div style={{ display: "flex", gap: T.space[2], alignItems: "center", flexWrap: "wrap", marginTop: T.space[3] }}>
                    {[{ k: "all", label: "All" }, { k: "Include", label: "✓ Include" }, { k: "Exclude", label: "✗ Exclude" }, { k: "Human Review", label: "⚠ Review" }, { k: "assoc", label: "↔ Assoc" }].map(c => (
                      <Button key={c.k} size="sm" variant="ghost" active={subDecision === c.k} onClick={() => setSubDecision(c.k)}>{c.label}</Button>
                    ))}
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: T.space[3] }}>
                      <Button size="sm" variant="ghost" disabled={!subFiltered.length}
                        onClick={shownAllSelected ? () => setSubSel(new Set()) : selectAllShown}>
                        {shownAllSelected ? "☑" : "☐"} Select all shown
                      </Button>
                      <span style={{ fontSize: T.size.caption, color: T.color.textMuted }}>
                        Showing <b style={{ color: T.color.text }}>{subFiltered.length}</b> of {totalPending}
                      </span>
                      <Button variant="primary" disabled={batchLoading} title={`Approve every ${subUser === "all" ? "pending" : subUser} submission (not just filtered)`}
                        onClick={() => subUser === "all" ? handleBatchApproveAll() : handleBatchApproveUser(subUser)}>
                        ✓ Approve all{subUser === "all" ? "" : ` (${subUser})`} · {subScopeCount}
                      </Button>
                      <Button variant="danger" disabled={batchLoading}
                        onClick={() => subUser === "all" ? handleBatchRejectAll() : handleBatchRejectUser(subUser)}>✗ Reject all</Button>
                    </div>
                  </div>
                </Card>

                {/* Bulk-action bar for checkbox selection */}
                {subSel.size > 0 && (
                  <div style={{ marginBottom: T.space[4], padding: `${T.space[3]}px ${T.space[4]}px`, display: "flex", alignItems: "center", gap: T.space[3], border: `1px solid ${T.color.accent}`, background: T.color.accentSoft, borderRadius: T.radius.lg, position: "sticky", top: 8, zIndex: 5 }}>
                    <span style={{ fontSize: T.size.sm, fontWeight: T.weight.semibold, color: T.color.text }}>{subSel.size} selected</span>
                    <div style={{ marginLeft: "auto", display: "flex", gap: T.space[2] }}>
                      <Button variant="primary" disabled={batchLoading} onClick={handleApproveSelected}>✓ Approve selected</Button>
                      <Button variant="danger" disabled={batchLoading} onClick={handleRejectSelected}>✗ Reject selected</Button>
                      <Button variant="ghost" onClick={() => setSubSel(new Set())}>Clear</Button>
                    </div>
                  </div>
                )}

                {/* Feed */}
                {subFiltered.length === 0 ? (
                  <div style={{ color: T.color.textMuted, fontSize: 13, textAlign: "center", marginTop: 40 }}>
                    No submissions match your search/filters. <button onClick={() => { setSubSearch(""); setSubDecision("all"); setSubUser("all"); }} style={{ background: "none", border: "none", color: T.color.accent, cursor: "pointer", fontSize: 13, fontFamily: "inherit", textDecoration: "underline" }}>Clear</button>
                  </div>
                ) : (
                  /* [SUBMISSIONS-REDESIGN] scroll window — toolbar + bulk bar stay fixed above; only the feed scrolls */
                  <div style={{ maxHeight: "calc(100vh - 300px)", minHeight: 240, overflowY: "auto", paddingRight: 6, marginRight: -6 }}>
                    {subFiltered.map(({ entry, dbKey, username }) => {
                      const k = selKey(username, dbKey, entry.id);
                      return (
                        <RowBoundary key={`${username}:${dbKey}:${entry.id}`} ro={entry.ro} id={entry.id} onReject={() => handleReject(entry, dbKey, username)}>
                          <EntryRow entry={entry} dbKey={dbKey} username={username}
                            onApprove={handleApprove} onReject={handleReject} onEditSignal={handleEditSignal}
                            selected={subSel.has(k)} onToggle={() => toggleSel(k)} />
                        </RowBoundary>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {!loading && activeTab === "master" && (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[6] }}>
            {[DB1_KEY, DB2_KEY, DB3_KEY].map(key => (
              <Card key={key}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: T.space[4] }}>
                  <Caps>{dbLabel[key]}</Caps>
                  <Num size={T.size.sm} color={T.color.textMuted}>{masterDb[key]?.length || 0} entries</Num>
                </div>
                {(masterDb[key] || []).length === 0
                  ? <div style={{ fontSize: T.size.sm, color: T.color.textFaint }}>Empty</div>
                  : <div style={{ maxHeight: 340, overflowY: "auto", border: `1px solid ${T.color.border}`, borderRadius: T.radius.lg }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.size.caption }}>
                        <thead>
                          <tr style={{ background: T.color.bgInset }}>
                            {(key === DB2_KEY
                              ? ["", "Associate", "Anchor", "Reasoning", "Date", ""]
                              : ["Decision", "Correction", "Signal", "Date", ""]
                            ).map((h, hi) => <th key={hi} style={{ padding: "8px 12px", textAlign: "left", fontSize: T.size.micro, color: T.color.textMuted, fontWeight: T.weight.semibold, textTransform: "uppercase", letterSpacing: T.tracking.caps }}>{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {[...(masterDb[key] || [])].reverse().map((e, i) => (
                            <tr key={i} style={{ borderTop: `1px solid ${T.color.borderFaint}` }}>
                              {key === DB2_KEY ? <>
                                <td style={{ padding: "7px 12px" }}><Tag color={e.inconclusive ? T.color.review : e.positive ? T.color.include : T.color.exclude}>{e.inconclusive ? "?" : e.positive ? "assoc" : "not"}</Tag></td>
                                <td style={{ padding: "7px 12px", color: T.color.textSecondary, maxWidth: 160 }}>{e.associateCorr}</td>
                                <td style={{ padding: "7px 12px", color: T.color.include, maxWidth: 160 }}>{e.anchorCorr}</td>
                                <td style={{ padding: "7px 12px", color: T.color.textMuted, maxWidth: 200 }}>{e.reasoning}</td>
                                <td style={{ padding: "7px 12px", color: T.color.textFaint, whiteSpace: "nowrap" }}>{new Date(e.date).toLocaleDateString()}</td>
                                <td style={{ padding: "7px 12px" }}><button onClick={() => deleteMasterEntry(key, e)} title="Delete — removes from master + brain (won't return on backfill)" style={{ background: T.color.excludeSoft, border: `1px solid ${T.color.excludeBorder}`, color: T.color.exclude, cursor: "pointer", fontSize: 12, fontWeight: 700, lineHeight: 1, padding: "3px 7px", borderRadius: T.radius.sm }}>✕</button></td>
                              </> : <>
                                <td style={{ padding: "7px 12px" }}>{["Include", "Exclude", "Human Review"].includes(e.decision) ? <Pill status={e.decision === "Include" ? "include" : e.decision === "Exclude" ? "exclude" : "review"} size="sm" /> : <Tag>{e.decision || "—"}</Tag>}</td>
                                <td style={{ padding: "7px 12px", color: T.color.textSecondary, maxWidth: 200 }}>{e.correction || <em style={{ color: T.color.textFaint }}>blank</em>}</td>
                                <td style={{ padding: "7px 12px", color: T.color.textMuted, maxWidth: 220 }}>{e.learningSignal || e.reasoning}</td>
                                <td style={{ padding: "7px 12px", color: T.color.textFaint, whiteSpace: "nowrap" }}>{new Date(e.date).toLocaleDateString()}</td>
                                <td style={{ padding: "7px 12px" }}><button onClick={() => deleteMasterEntry(key, e)} title="Delete — removes from master + brain (won't return on backfill)" style={{ background: T.color.excludeSoft, border: `1px solid ${T.color.excludeBorder}`, color: T.color.exclude, cursor: "pointer", fontSize: 12, fontWeight: 700, lineHeight: 1, padding: "3px 7px", borderRadius: T.radius.sm }}>✕</button></td>
                              </>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                }
              </Card>
            ))}
          </div>
        )}

        {!loading && activeTab === "projects" && (
          <div>
            {allProjects.reduce((n, u) => n + (u.projects?.length || 0), 0) === 0
              ? <div style={{ color: C.muted, fontSize: 13, textAlign: "center", marginTop: 40 }}>No projects yet across the team.</div>
              : allProjects.filter(u => (u.projects || []).length).sort((a, b) => a.username.localeCompare(b.username)).map(u => (
                <div key={u.username} style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 30, height: 30, borderRadius: "50%", background: `${C.accent}20`, border: `1px solid ${C.accent}40`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: C.accent }}>{u.username[0]?.toUpperCase()}</div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{u.username}</div>
                    <div style={{ fontSize: 11, color: C.muted }}>{(u.projects || []).length} project{(u.projects || []).length !== 1 ? "s" : ""}</div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 12 }}>
                    {[...(u.projects || [])].sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || "")).map(p => {
                      const reviewed = (p.totalLines || 0) - (p.pending || 0);
                      const pct = p.totalLines ? Math.round((reviewed / p.totalLines) * 100) : 0;
                      return (
                        <Card key={p.projectId} style={{ padding: "12px 14px", opacity: p.archived ? 0.6 : 1 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                            <div style={{ fontSize: T.size.sm, fontWeight: T.weight.semibold, color: T.color.text, flex: 1, wordBreak: "break-word" }}>{p.name || "Untitled"}</div>
                            {p.archived && <Tag>ARCHIVED</Tag>}
                          </div>
                          <div style={{ fontSize: T.size.caption, color: T.color.textMuted, marginBottom: 6, wordBreak: "break-word" }}>{p.file || "no file"}</div>
                          <div style={{ display: "flex", gap: 10, fontSize: T.size.caption, marginBottom: 6 }}>
                            <span style={{ color: T.color.include }}>✓ {p.includes || 0}</span><span style={{ color: T.color.exclude }}>✗ {p.excludes || 0}</span><span style={{ color: T.color.review }}>⚠ {p.pending || 0}</span>
                            {p.uniqueIncludedROs ? <span style={{ color: T.color.accent }}>{p.uniqueIncludedROs} ROs</span> : null}
                          </div>
                          <div style={{ background: T.color.bgInset, borderRadius: 999, height: 5, overflow: "hidden" }}><div style={{ height: "100%", borderRadius: 999, background: (p.pending || 0) > 0 ? T.color.review : T.color.include, width: `${pct}%` }} /></div>
                          <div style={{ fontSize: T.size.micro, color: T.color.textFaint, marginTop: 5 }}>updated {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : "—"}</div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}

        {!loading && activeTab === "corpus" && (
          <CorpusTab masterDb={masterDb} onPromoted={load} />
        )}

        {!loading && activeTab === "confidence" && (
          <ConfidenceTab allProjects={allProjects} />
        )}

        {activeTab === "brain" && (
          <BrainConsole />
        )}

        {activeTab === "users" && (
          <UsersTab />
        )}

        {!loading && activeTab === "seed" && (
          <SeedKBTab masterDb={masterDb} onSeeded={() => { load(); setActiveTab("master"); }} />
        )}
      </div>
    </div>
  );
}

// ── [ACCOUNTS 2026-07-13] Users tab — per-person accounts with roles ─────────
// Accounts live in DynamoDB `users-v1` (scrypt-hashed, managed via api/db.js). Create each
// account with the SAME name the person already used on the classifier, so their existing
// projects/sessions stay attached. Admin accounts see an "Admin →" link on the classifier
// and open this panel with no second login. The legacy shared TEAM/ADMIN passwords keep
// working until Blake rotates them (that's the kill switch for the old model).
function UsersTab() {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState(""); const [msg, setMsg] = useState("");
  const [nu, setNu] = useState(""); const [np, setNp] = useState(""); const [nr, setNr] = useState("team");
  const [busy, setBusy] = useState("");
  const [resetFor, setResetFor] = useState(null); const [resetPw, setResetPw] = useState("");  // [WPB1 2026-08-04] in-page password reset (was window.prompt)
  const load = async () => { setErr(""); try { const r = await apiFetch("list-users"); setUsers(r.value || []); } catch (e) { setErr("Load failed: " + e.message); setUsers([]); } };
  useEffect(() => { load(); }, []);
  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 4000); };
  const create = async () => {
    if (!nu.trim()) { setErr("Enter a username."); return; }
    if ((np || "").length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy("create"); setErr("");
    try { await apiFetch("create-user", { newUsername: nu.trim(), password: np, role: nr }); flash(`✓ Account "${nu.trim()}" created (${nr})`); setNu(""); setNp(""); setNr("team"); await load(); }
    catch (e) { setErr("Create failed: " + e.message); }
    setBusy("");
  };
  // [WPB1 2026-08-04] Password reset is an in-page row, not window.prompt — Chrome silently
  // suppresses those dialogs (the classifier's "dead + New Project button", 2026-07-20), and a
  // prompt() box shows the new password in cleartext. Type it into a masked field in the card.
  const reset = async (u) => {
    const pw = resetPw;
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    setBusy(u.username); setErr("");
    try { await apiFetch("set-user-password", { targetUser: u.username, password: pw }); flash(`✓ Password reset for ${u.username}`); setResetFor(null); setResetPw(""); }
    catch (e) { setErr("Reset failed: " + e.message); }
    setBusy("");
  };
  const toggleDisabled = async (u) => {
    if (!u.disabled && !window.confirm(`Disable "${u.username}"? They can't log in until re-enabled (their projects and history are kept).`)) return;
    setBusy(u.username);
    try { await apiFetch("set-user-disabled", { targetUser: u.username, disabled: !u.disabled }); await load(); flash(u.disabled ? `✓ ${u.username} re-enabled` : `✓ ${u.username} disabled`); }
    catch (e) { setErr("Update failed: " + e.message); }
    setBusy("");
  };
  const setRole = async (u, role) => {
    if (role === u.role) return;
    setBusy(u.username);
    try { await apiFetch("set-user-role", { targetUser: u.username, role }); await load(); flash(`✓ ${u.username} is now ${role}`); }
    catch (e) { setErr("Update failed: " + e.message); }
    setBusy("");
  };
  // [WPT1 2026-08-06] Put a team member into (or out of) a training level. Two things about the
  // timing are worth saying out loud in the flash, because they are what someone will ask:
  // the never-learn half applies to their very next action, and the AI/brain half follows their
  // next sign-in (that is what re-mints the token api/claude.js reads).
  const setTraining = async (u, level) => {
    const want = level || null;
    if ((u.trainingMode || null) === want) return;
    setBusy(u.username);
    try {
      await apiFetch("set-user-training", { targetUser: u.username, trainingMode: want });
      await load();
      flash(want
        ? `✓ ${u.username} is in training ${want} — their decisions stop reaching the KB immediately; AI/brain switch off at their next sign-in`
        : `✓ ${u.username} is out of training mode — they should sign out and back in to get AI and the brain back`);
    }
    catch (e) { setErr("Update failed: " + e.message); }
    setBusy("");
  };
  return (
    <div>
      <div style={{ fontSize: T.size.sm, color: T.color.textMuted, marginBottom: T.space[5], lineHeight: 1.5 }}>
        Personal accounts — everyone logs in on the classifier with their <b>name + their own password</b>; their uplift projects save under their account.
        <b> Use the same name each person already used</b> so their existing projects stay attached. Admins get the <b>Admin →</b> link on the classifier (no second login here).
      </div>
      <Card style={{ marginBottom: T.space[6] }}>
        <div style={{ fontSize: T.size.md, fontWeight: T.weight.semibold, color: T.color.text, marginBottom: 10 }}>Create account</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ width: 200 }}><Input value={nu} onChange={e => setNu(e.target.value)} placeholder="Name (e.g. Blake)" /></div>
          <div style={{ width: 220 }}><Input type="password" value={np} onChange={e => setNp(e.target.value)} placeholder="Password (min 8 chars)" /></div>
          <div style={{ width: 150 }}><Select value={nr} onChange={e => setNr(e.target.value)} options={[{ value: "team", label: "Team" }, { value: "admin", label: "Admin" }]} /></div>
          <Button variant="primary" onClick={create} disabled={busy === "create"}>{busy === "create" ? "Creating…" : "+ Create"}</Button>
        </div>
      </Card>
      {err && <div style={{ color: T.color.exclude, fontSize: T.size.sm, marginBottom: 10 }}>⚠ {err}</div>}
      {msg && <div style={{ color: T.color.include, fontSize: T.size.sm, fontWeight: T.weight.semibold, marginBottom: 10 }}>{msg}</div>}
      {users == null ? <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>
        : users.length === 0 ? <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 13 }}>No accounts yet — create yours first (name "Blake", role Admin), then one per team member.</div>
        : users.map(u => (
          <div key={u.username} style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 8, background: C.card, opacity: u.disabled ? 0.55 : 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ width: 28, height: 28, borderRadius: "50%", background: `${C.accent}30`, border: `1px solid ${C.accent}50`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: C.accent }}>{u.username?.[0]?.toUpperCase()}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: T.size.sm, fontWeight: T.weight.semibold, color: C.text }}>{u.username}</span>
                <Tag color={u.role === "admin" ? T.color.accent : T.color.textMuted} style={{ marginLeft: 8 }}>{u.role}</Tag>
                {u.disabled && <Tag color={T.color.exclude} style={{ marginLeft: 6 }}>disabled</Tag>}
                {u.trainingMode && <Tag color={T.color.review} style={{ marginLeft: 6 }}>{`🎓 training ${u.trainingMode}`}</Tag>}
                {u.createdAt && <span style={{ marginLeft: 10, fontSize: T.size.caption, color: C.dim }}>since {new Date(u.createdAt).toLocaleDateString()}</span>}
              </div>
              <Select value={u.role} onChange={e => setRole(u, e.target.value)} options={[{ value: "team", label: "Team" }, { value: "admin", label: "Admin" }]} style={{ width: 110 }} />
              {/* [WPT1 2026-08-06] Training level, beside the role. Admin accounts ignore the flag
                  (the server refuses to set it on one), so the control is a team-only control. */}
              {u.role !== "admin" && (
                <Select value={u.trainingMode || ""} onChange={e => setTraining(u, e.target.value)}
                  options={[{ value: "", label: "Not training" }, { value: "T1", label: "T1 — bare" }, { value: "T2", label: "T2 — rules only" }]} style={{ width: 150 }} />
              )}
              <Button variant="secondary" onClick={() => { setErr(""); setResetPw(""); setResetFor(resetFor === u.username ? null : u.username); }} disabled={busy === u.username}>Reset password</Button>
              <Button variant="ghost" onClick={() => toggleDisabled(u)} disabled={busy === u.username}>{u.disabled ? "Re-enable" : "Disable"}</Button>
            </div>
            {/* [WPB1 2026-08-04] in-page reset row — replaces window.prompt (masked, Enter submits, Esc cancels) */}
            {resetFor === u.username && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontSize: T.size.caption, color: C.muted }}>New password for <b>{u.username}</b> (min 8 characters)</span>
                <div style={{ width: 220 }}>
                  <Input type="password" autoFocus value={resetPw} onChange={e => setResetPw(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") reset(u); if (e.key === "Escape") { setResetFor(null); setResetPw(""); } }}
                    placeholder="New password" />
                </div>
                <Button variant="primary" onClick={() => reset(u)} disabled={busy === u.username || resetPw.length < 8}>{busy === u.username ? "Saving…" : "Set password"}</Button>
                <Button variant="ghost" onClick={() => { setResetFor(null); setResetPw(""); }}>Cancel</Button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

// [WPB1 2026-08-04] Validate the stored admin token BEFORE rendering the panel. Before this, a
// 30-day account token that had expired (or a stale/garbage localStorage value) rendered the full
// admin UI, which then 401'd on every call — a panel that looks alive and does nothing. `whoami`
// (api/db.js) resolves the token server-side and reports { username, isAdmin, legacy }.
// Three outcomes: valid admin → panel; 401/403 or a non-admin identity → token cleared, login
// screen with the reason; network/5xx error → token KEPT and the panel renders (a connection blip
// must not force everyone to re-type the password; the panel surfaces its own load error).
export default function AdminApp() {
  const [authState, setAuthState] = useState(() => getToken() ? "checking" : "out");  // checking | in | out
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (authState !== "checking") return;
    let cancelled = false;
    (async () => {
      const bounce = (why) => { localStorage.removeItem("ez-wins-admin-token"); setNotice(why); setAuthState("out"); };
      try {
        const res = await fetch("/api/db", {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-team-token": getToken() },
          body: JSON.stringify({ action: "whoami" }),
        });
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) return bounce("Your admin session expired — sign in again.");
        if (!res.ok) { setAuthState("in"); return; }   // server trouble, not a bad token
        const { value } = await res.json();
        if (cancelled) return;
        if (!value?.isAdmin) return bounce("That sign-in isn't an admin account — use the admin password.");
        setAuthState("in");
      } catch {
        if (!cancelled) setAuthState("in");            // offline / network blip — keep the token
      }
    })();
    return () => { cancelled = true; };
  }, [authState]);

  if (authState === "checking") return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, color: C.muted, fontSize: 13, fontFamily: T.font.sans }}>Checking your session…</div>
  );
  if (authState === "out") return <AdminLogin notice={notice} onAuth={() => { setNotice(""); setAuthState("in"); }} />;
  return <AdminPanel />;
}
